import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Drawer,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  Tooltip,
  Tabs,
  Tab,
  Menu,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckIcon from '@mui/icons-material/Check';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import SyntaxHighlighter from "react-syntax-highlighter";
import atomOneLight from "react-syntax-highlighter/dist/cjs/styles/hljs/atom-one-light";
import ReactMarkdown from "react-markdown";
import axiosInstance from "../axiosInstance";
import { isAdminUser } from "../config/adminUsers";
import { getUserEmail } from "../utils/getUserEmail";
import { marked } from "marked";
import LlmTraceDetails from "./LlmTraceDetails";
import {
  calculateWebSearchCost,
  getSearchCallCount,
  WEB_SEARCH_PRICE_PER_1000_CALLS_USD,
} from "../utils/llmCost";

// OpenAI standard pricing per 1M tokens (as of July 2026)
const OPENAI_PRICING = {
  // GPT-6 Standard short-context rates verified October 2026:
  // https://developers.openai.com/api/docs/pricing
  "gpt-6-astra": {
    input: 10.00,
    output: 50.00,
    reasoning: 50.00
  },
  "gpt-6.1-sol": {
    input: 2.00,
    output: 10.00,
    reasoning: 10.00
  },
  "gpt-6-sol": {
    input: 2.00,
    output: 10.00,
    reasoning: 10.00
  },
  "gpt-6-luna": {
    input: 0.10,
    output: 0.50,
    reasoning: 0.50
  },
  "gpt-4o": {
    input: 2.50,
    output: 10.00
  },
  "gpt-4o-mini": {
    input: 0.15,
    output: 0.60
  },
  "gpt-4.1": {
    input: 2.00,
    output: 8.00
  },
  "gpt-4.1-mini": {
    input: 0.40,
    output: 1.60
  },
  "gpt-5.6-sol": {
    input: 5.00,
    output: 30.00,
    reasoning: 30.00
  },
  "gpt-5.6-terra": {
    input: 2.00,
    output: 12.00,
    reasoning: 12.00
  },
  "gpt-5.6-luna": {
    input: 0.20,
    output: 1.20,
    reasoning: 1.20
  },
  "gpt-5.4": {
    input: 2.50,
    output: 15.00,
    reasoning: 15.00
  },
  "gpt-5.4-mini": {
    input: 0.75,
    output: 4.50,
    reasoning: 4.50
  },
  "gpt-5.4-nano": {
    input: 0.20,
    output: 1.25,
    reasoning: 1.25
  }
};

// Exchange rates (approximate as of September 2025) - Not used currently
const EXCHANGE_RATES = {
  USD_TO_AUD: 1.56 // 1 USD = 1.56 AUD (approximate)
};

const DEFAULT_TEST_PROMPT_MODEL = "gpt-4o-mini";
const DEFAULT_REASONING_EFFORT_LEVEL = "low";
const REASONING_EFFORT_LEVEL_OPTIONS = ["none", "minimal", "low", "medium", "high", "xhigh"];
const WEB_SEARCH_OPTIONS = [
  { value: "true", label: "true" },
  { value: "false", label: "false" },
];

const TIMELINE_COLORS = [
  { fill: "#ff6b70", shadow: "rgba(255, 107, 112, 0.26)" },
  { fill: "#4fc7c3", shadow: "rgba(79, 199, 195, 0.26)" },
  { fill: "#6f8ff7", shadow: "rgba(111, 143, 247, 0.24)" },
  { fill: "#a776e8", shadow: "rgba(167, 118, 232, 0.24)" },
  { fill: "#f3ae4e", shadow: "rgba(243, 174, 78, 0.24)" },
  { fill: "#ec75ad", shadow: "rgba(236, 117, 173, 0.24)" },
];

const RESPONSE_JSON_STYLE = {
  ...atomOneLight,
  "hljs-attr": {
    ...atomOneLight["hljs-attr"],
    fontWeight: 700,
  },
  "hljs-string": {
    ...atomOneLight["hljs-string"],
    color: "#111827",
  },
};

function getTimelineColor(name = "") {
  const hash = [...name].reduce(
    (value, character) => ((value * 31) + character.charCodeAt(0)) >>> 0,
    0
  );
  return TIMELINE_COLORS[hash % TIMELINE_COLORS.length];
}

function isReasoningModel(model) {
  return typeof model === "string" && /^gpt-[56]/i.test(model);
}

function getReasoningEffortOptions(model) {
  const normalizedModel = (model || "").toLowerCase();
  if (normalizedModel.startsWith("gpt-6")) {
    const levels = ["low", "medium", "high", "xhigh", "max"];
    return normalizedModel.startsWith("gpt-6-astra") || normalizedModel.startsWith("gpt-6.1-sol")
      ? levels
      : ["none", ...levels];
  }
  return REASONING_EFFORT_LEVEL_OPTIONS;
}

function normalizeReasoningEffort(model, effort) {
  return getReasoningEffortOptions(model).includes(effort)
    ? effort
    : DEFAULT_REASONING_EFFORT_LEVEL;
}

function isGpt4FamilyModel(model) {
  return typeof model === "string" && model.toLowerCase().startsWith("gpt-4");
}

function getReasoningTokenPrice(model, pricing) {
  if (isGpt4FamilyModel(model)) {
    return 0;
  }
  return pricing.reasoning ?? pricing.output;
}

function getPlaceholderValue(placeholders, tagName) {
  if (!Array.isArray(placeholders) || !tagName) {
    return undefined;
  }

  const matchedPlaceholder = placeholders.find(
    (placeholder) => (placeholder?.TagName || placeholder?.tagName) === tagName
  );

  return matchedPlaceholder?.Value ?? matchedPlaceholder?.value;
}

function normalizeBooleanLikeValue(value) {
  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === "true") return true;
    if (normalizedValue === "false") return false;
  }

  return Boolean(value);
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Helper to unstringify a JSON string.
function unescapeJsonString(str) {
  try {
    if (!str || typeof str !== "string") return str;
    if (str[0] !== '"') {
      return JSON.parse('"' + str.replace(/"/g, '\\"') + '"');
    } else {
      return JSON.parse(str);
    }
  } catch {
    return str;
  }
}

function parseJsonDisplayValue(value) {
  if (typeof value !== "string") {
    return value;
  }

  let parsedValue = value;

  // Parse the JSON before unescaping its contents. Unescaping first turns valid
  // sequences such as `\n` into literal control characters and breaks prompts
  // containing multiline text. A second pass supports double-encoded payloads.
  for (let pass = 0; pass < 2 && typeof parsedValue === "string"; pass += 1) {
    try {
      parsedValue = JSON.parse(parsedValue);
    } catch {
      const unescapedValue = unescapeJsonString(parsedValue);
      return unescapedValue;
    }
  }

  return parsedValue;
}

function normalizeNestedJsonValues(value) {
  if (Array.isArray(value)) {
    return value.map(normalizeNestedJsonValues);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        normalizeNestedJsonValues(nestedValue),
      ])
    );
  }

  if (typeof value === "string") {
    const unescapedValue = unescapeJsonString(value);
    const trimmedValue = unescapedValue.trim();
    const looksLikeJsonContainer =
      (trimmedValue.startsWith("{") && trimmedValue.endsWith("}")) ||
      (trimmedValue.startsWith("[") && trimmedValue.endsWith("]"));

    if (looksLikeJsonContainer) {
      try {
        return normalizeNestedJsonValues(JSON.parse(unescapedValue));
      } catch {
        return unescapedValue;
      }
    }

    return unescapedValue;
  }

  return value;
}

function unescapeFormattedJsonContent(value) {
  return value
    .replace(/\\+r\\+n/g, "\n")
    .replace(/\\+[nr]/g, "\n")
    .replace(/\\+t/g, "\t")
    .replace(/\\+"/g, '"');
}

function formatJsonForDisplay(value) {
  const parsedValue = parseJsonDisplayValue(value);
  const normalizedValue = normalizeNestedJsonValues(parsedValue);

  const formattedValue = normalizedValue && typeof normalizedValue === "object"
    ? JSON.stringify(normalizedValue, null, 2)
    : typeof normalizedValue === "string"
      ? normalizedValue
      : JSON.stringify(normalizedValue);

  return typeof formattedValue === "string"
    ? unescapeFormattedJsonContent(formattedValue)
    : formattedValue;
}

function getJsonSyntaxDisplayValue(value) {
  const parsedValue = normalizeNestedJsonValues(parseJsonDisplayValue(value));

  if (!parsedValue || typeof parsedValue !== "object") {
    return null;
  }

  return JSON.stringify(parsedValue, null, 2);
}

function getLlmRequestFields(value) {
  const parsedValue = normalizeNestedJsonValues(parseJsonDisplayValue(value));

  if (
    !parsedValue ||
    typeof parsedValue !== "object" ||
    Array.isArray(parsedValue) ||
    !(Object.prototype.hasOwnProperty.call(parsedValue, "prompt") &&
      Object.prototype.hasOwnProperty.call(parsedValue, "userMessage"))
  ) {
    return null;
  }

  return parsedValue;
}

function extractMessageByRole(raw, role) {
  const regex = new RegExp(
    `<message\\b[^>]*\\brole=(?:'|")${role}(?:'|")[^>]*>([\\s\\S]*?)<\\/message>`,
    "i"
  );
  const m = regex.exec(raw);
  return m ? m[1].trim() : null;
}

// Headers to exclude from curl (browser fingerprinting / auto-computed)
const CURL_SKIP_HEADERS = [
  'Accept-Encoding', 'Content-Length', 'Host', 'User-Agent',
  'sec-ch-ua', 'sec-ch-ua-mobile', 'sec-ch-ua-platform',
  'sec-fetch-site', 'sec-fetch-mode', 'sec-fetch-dest', 'sec-gpc', 'priority'
];

function extractRequestGroup(logs, bodyLogIndex) {
  let headersLog = null;
  let startingLog = null;
  for (let i = bodyLogIndex - 1; i >= Math.max(0, bodyLogIndex - 15); i--) {
    const msg = logs[i].message || "";
    if (!headersLog && msg.startsWith("Request Headers:")) {
      headersLog = logs[i];
    }
    if (!startingLog && msg.startsWith("Request starting")) {
      startingLog = logs[i];
    }
    if (headersLog && startingLog) break;
  }
  return { startingLog, headersLog, bodyLog: logs[bodyLogIndex] };
}

function buildCurlCommand(startingLog, headersLog, bodyLog) {
  let method = "POST";
  let url = "";

  if (startingLog) {
    const match = startingLog.message.match(/Request starting\s+\S+\s+(\w+)\s+(\S+)/);
    if (match) {
      method = match[1];
      url = match[2].replace(/^http:/, "https:");
    }
  }

  let headers = {};
  if (headersLog) {
    const jsonStr = headersLog.message.replace(/^Request Headers:\s*/, "");
    try { headers = JSON.parse(jsonStr); } catch {}
  }

  let body = "";
  if (bodyLog) {
    body = bodyLog.message.replace(/^Request Body:\s*/, "");
  }

  let curl = `curl -X ${method}`;
  if (url) curl += ` '${url}'`;

  for (const [key, value] of Object.entries(headers)) {
    if (!CURL_SKIP_HEADERS.some(h => h.toLowerCase() === key.toLowerCase())) {
      curl += ` \\\n  -H '${key}: ${value}'`;
    }
  }

  if (body && method !== "GET") {
    try {
      const parsed = JSON.parse(body);
      curl += ` \\\n  -d '${JSON.stringify(parsed)}'`;
    } catch {
      curl += ` \\\n  -d '${body}'`;
    }
  }

  return curl;
}

const BodyEditor = React.memo(({ value, onChange }) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <TextField
      fullWidth
      multiline
      minRows={10}
      maxRows={10}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => onChange(localValue)}
      placeholder="Request body (JSON)..."
      inputProps={{ spellCheck: false }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "12px",
          backgroundColor: "#1e1e2e",
          fontFamily: "monospace",
          fontSize: "0.78rem",
          lineHeight: 1.6,
          color: "#cdd6f4",
          "& fieldset": { borderColor: "transparent" },
          "&:hover fieldset": { borderColor: "rgba(255, 132, 76, 0.30)" },
          "&.Mui-focused fieldset": { borderColor: "#f97316" },
        },
        "& .MuiInputBase-input": {
          overflow: "auto !important",
        },
      }}
    />
  );
});

const ENV_URLS = [
  {
    label: "Localhost",
    url: "https://localhost:7288",
    defaultUrl: "https://localhost:7288/api/chat",
  },
  { label: "Dev", url: "https://dev-greenback-app-chat.azurewebsites.net" },
  { label: "Staging", url: "https://prod-greenback-app-chat-preview.azurewebsites.net" },
  { label: "Prod", url: "https://prod-greenback-app-chat.azurewebsites.net" },
];

const UrlEditor = React.memo(({ value, onChange }) => {
  const [localValue, setLocalValue] = useState(value);
  const [envMenuAnchor, setEnvMenuAnchor] = useState(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const detectCurrentEnv = () => {
    const current = localValue || "";
    return ENV_URLS.find((env) => current.startsWith(env.url))?.label || null;
  };

  const handleEnvSelect = (env) => {
    const current = localValue || "";
    const matchedEnv = ENV_URLS.find((e) => current.startsWith(e.url));
    let newUrl;
    if (matchedEnv) {
      newUrl = current.replace(matchedEnv.url, env.url);
    } else {
      const urlMatch = current.match(/^(https?:\/\/[^/]+)(.*)/);
      if (urlMatch) {
        newUrl = env.url + urlMatch[2];
      } else {
        newUrl = env.defaultUrl || env.url;
      }
    }
    setLocalValue(newUrl);
    onChange(newUrl);
    setEnvMenuAnchor(null);
  };

  return (
    <>
      <TextField
        fullWidth
        size="small"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={() => onChange(localValue)}
        placeholder="https://..."
        inputProps={{ spellCheck: false }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Switch environment">
                <IconButton
                  size="small"
                  onClick={(e) => setEnvMenuAnchor(e.currentTarget)}
                  sx={{
                    color: "#f97316",
                    "&:hover": { backgroundColor: "#fff7f1" },
                  }}
                >
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: "#fffaf7",
            fontFamily: "monospace",
            fontSize: "0.82rem",
            "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" },
            "&:hover fieldset": { borderColor: "#f97316" },
            "&.Mui-focused fieldset": { borderColor: "#f97316" },
          },
        }}
      />
      <Menu
        anchorEl={envMenuAnchor}
        open={Boolean(envMenuAnchor)}
        onClose={() => setEnvMenuAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            minWidth: 200,
          },
        }}
      >
        {ENV_URLS.map((env) => {
          const isActive = detectCurrentEnv() === env.label;
          return (
            <MenuItem
              key={env.label}
              onClick={() => handleEnvSelect(env)}
              selected={isActive}
              sx={{
                fontSize: "0.84rem",
                py: 1,
                "&.Mui-selected": { backgroundColor: "#fff7f1" },
                "&.Mui-selected:hover": { backgroundColor: "#ffedd5" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {isActive ? <CheckIcon fontSize="small" sx={{ color: "#f97316" }} /> : null}
              </ListItemIcon>
              <ListItemText
                primary={env.label}
                secondary={env.defaultUrl || env.url}
                primaryTypographyProps={{ fontWeight: 700, fontSize: "0.84rem" }}
                secondaryTypographyProps={{ fontSize: "0.72rem", fontFamily: "monospace", noWrap: true }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
});

function JsonSyntaxBlock({ value, testId, compact = false }) {
  return (
    <Box
      data-testid={testId}
      sx={{
        height: compact ? 120 : "auto",
        minHeight: 0,
        maxHeight: compact ? 160 : "72vh",
        overflow: "auto",
        resize: compact ? "none" : "vertical",
        border: "1px solid rgba(15, 23, 42, 0.10)",
        borderRadius: "12px",
        backgroundColor: "#fbfcfe",
        boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.035)",
        "& pre": {
          minHeight: "100%",
          boxSizing: "border-box",
          margin: "0 !important",
        },
      }}
    >
      <SyntaxHighlighter
        language="json"
        style={RESPONSE_JSON_STYLE}
        wrapLongLines
        customStyle={{
          margin: 0,
          padding: "16px",
          background: "#fbfcfe",
          fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
          fontSize: "0.78rem",
          lineHeight: 1.6,
        }}
        codeTagProps={{
          style: {
            fontFamily: "inherit",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          },
        }}
      >
        {value}
      </SyntaxHighlighter>
    </Box>
  );
}

function FormattedTextBlock({ value }) {
  return (
    <Box
      data-testid="formatted-text-response"
      sx={{
        height: "auto",
        minHeight: 0,
        maxHeight: "72vh",
        overflow: "auto",
        resize: "vertical",
        px: 2,
        py: 1.5,
        border: "1px solid rgba(15, 23, 42, 0.10)",
        borderRadius: "12px",
        backgroundColor: "#fbfcfe",
        color: "#27303d",
        fontSize: "0.88rem",
        lineHeight: 1.65,
        boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.035)",
        "& h1, & h2, & h3, & h4": {
          mt: 1.5,
          mb: 0.75,
          color: "#202531",
          fontWeight: 800,
          lineHeight: 1.3,
        },
        "& h1": { fontSize: "1.4rem" },
        "& h2": { fontSize: "1.2rem" },
        "& h3, & h4": { fontSize: "1rem" },
        "& p": { my: 1 },
        "& ul, & ol": { my: 1, pl: 3 },
        "& li": { mb: 0.4 },
        "& strong": { color: "#202531", fontWeight: 800 },
        "& code": {
          px: 0.45,
          py: 0.15,
          borderRadius: "5px",
          backgroundColor: "#eef1f5",
          fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
          fontSize: "0.82em",
        },
        "& pre": {
          overflowX: "auto",
          p: 1.25,
          borderRadius: "10px",
          backgroundColor: "#eef1f5",
        },
      }}
    >
      <ReactMarkdown>{value || ""}</ReactMarkdown>
    </Box>
  );
}

function TimelineResponseTooltip({ response }) {
  return (
    <Box sx={{ width: 560, maxWidth: "calc(100vw - 48px)" }}>
      <Typography
        component="div"
        sx={{ mb: 0.75, color: "#fed7aa", fontSize: "0.72rem", fontWeight: 800 }}
      >
        Response
      </Typography>
      <Box
        sx={{
          maxHeight: 360,
          overflow: "auto",
          color: "#f8fafc",
          fontSize: "0.82rem",
          lineHeight: 1.55,
          overflowWrap: "anywhere",
          "& > :first-of-type": { mt: 0 },
          "& > :last-child": { mb: 0 },
          "& p": { my: 0.75 },
          "& ul, & ol": { my: 0.75, pl: 2.5 },
          "& li": { mb: 0.25 },
          "& code": {
            px: 0.4,
            py: 0.1,
            borderRadius: "4px",
            backgroundColor: "rgba(255, 255, 255, 0.14)",
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            fontSize: "0.9em",
          },
          "& pre": {
            overflowX: "auto",
            p: 1,
            borderRadius: "8px",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
      >
        <ReactMarkdown>{response || "No formatted response was recorded."}</ReactMarkdown>
      </Box>
    </Box>
  );
}

function SplitLlmRequestFields({ request, onChange }) {
  const toEditableText = (value) =>
    typeof value === "string" ? value : JSON.stringify(value ?? "", null, 2);

  const updateField = (fieldName, fieldValue) => {
    onChange(JSON.stringify({ ...request, [fieldName]: fieldValue }, null, 2));
  };

  return (
    <Box
      data-testid="split-llm-request-fields"
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 0,
      }}
    >
      {[
        { key: "prompt", label: "LLM Request Formated ( Prompt )" },
        { key: "userMessage", label: "LLM Request Formated ( User Message )" },
      ].map(({ key, label }) => {
        const fieldValue = toEditableText(request[key]);

        return (
          <Box
            key={key}
            sx={{
              minWidth: 0,
            }}
          >
            <CollapsibleField
              label={label}
              value={fieldValue}
              inputAriaLabel={key}
              alwaysExpanded={key === "userMessage"}
              fitContent={key === "userMessage"}
              onChange={(newValue) => updateField(key, newValue)}
            />
          </Box>
        );
      })}
    </Box>
  );
}

function CollapsibleField({
  label,
  value,
  onChange,
  jsonMode = false,
  alwaysExpanded = false,
  highlightJson = false,
  formatText = false,
  inputAriaLabel,
  fitContent = false,
}) {
  const [expanded, setExpanded] = useState(alwaysExpanded);
  const [fieldValue, setFieldValue] = useState(value || "");

  useEffect(() => {
    setFieldValue(value || "");
  }, [value]);

  const val = unescapeJsonString(fieldValue);
  const systemMessage = extractMessageByRole(val, "system");
  const userMessage = extractMessageByRole(val, "user");

  const displayValue = (() => {
    if (!jsonMode || !expanded) return fieldValue;
    return formatJsonForDisplay(fieldValue);
  })();
  const highlightedJsonValue = highlightJson && expanded
    ? getJsonSyntaxDisplayValue(fieldValue)
    : null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: "bold" }}>
          {label}
        </Typography>
        {!alwaysExpanded && (
          <IconButton
            aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`}
            onClick={() => setExpanded((p) => !p)}
            size="small"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>
      {expanded && highlightedJsonValue !== null ? (
        <JsonSyntaxBlock
          value={highlightedJsonValue}
          testId="json-syntax-highlight"
        />
      ) : expanded && formatText ? (
        <FormattedTextBlock value={displayValue} />
      ) : expanded ? (
        <TextField
          fullWidth
          multiline
          minRows={fitContent ? 1 : 3}
          maxRows={12}
          variant="outlined"
          value={displayValue}
          inputProps={inputAriaLabel ? { "aria-label": inputAriaLabel } : undefined}
          sx={{
            "& textarea": {
              minHeight: fitContent ? "auto !important" : "64px !important",
              maxHeight: "72vh",
              overflow: "auto !important",
              resize: "vertical !important",
            },
          }}
          onChange={(e) => {
            const newVal = e.target.value;
            setFieldValue(newVal);
            onChange && onChange(newVal);
          }}
        />
      ) : (
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: 1,
            p: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Typography variant="body2">{displayValue || "(empty)"}</Typography>
        </Box>
      )}
    </Box>
  );
}

function getResponseMetrics(agent, formatNumberValue, formatCurrencyValue) {
  const promptContent = agent?.promptContent || {};
  const cost = calculateCost(
    promptContent.llmModel,
    promptContent.promptTokensFromResponse,
    promptContent.completionTokensFromResponse,
    promptContent.reasoningTokensFromResponse,
    getSearchCallCount(promptContent)
  );
  const displayValue = (value) =>
    value === undefined || value === null || value === ""
      ? "N/A"
      : String(value);
  const pendingValue = agent ? null : "Loading…";
  const metricValue = (value) => pendingValue || value;

  return [
    ["Agent", metricValue(agent?.agentName || "Unknown Agent")],
    ["Duration", metricValue(`${agent?.durationMs || 0}ms`)],
    ["Model", promptContent.llmModel || "N/A"],
    ["Type", promptContent.type || "N/A"],
    [
      "Timestamp",
      metricValue(
        agent?.["@timestamp"]
          ? new Date(agent["@timestamp"]).toLocaleString()
          : "N/A"
      ),
    ],
    ["Max Tokens", metricValue(displayValue(promptContent.maxTokens))],
    ["Response Format", metricValue(displayValue(promptContent.responseFormat))],
    ["Draft", metricValue(displayValue(promptContent.draft))],
    ["Prompt Tokens", metricValue(formatNumberValue(promptContent.promptTokensFromResponse || 0))],
    ["Completion Tokens", metricValue(formatNumberValue(promptContent.completionTokensFromResponse || 0))],
    ["Reasoning Tokens", metricValue(formatNumberValue(promptContent.reasoningTokensFromResponse || 0))],
    ["Search Calls", metricValue(formatNumberValue(getSearchCallCount(promptContent)))],
    ["Input Cost", metricValue(formatCurrencyValue(cost.inputCost, "USD"))],
    ["Output Cost", metricValue(formatCurrencyValue(cost.outputCost, "USD"))],
    ["Reasoning Cost", metricValue(formatCurrencyValue(cost.reasoningCost, "USD"))],
    ["Web Search Cost", metricValue(formatCurrencyValue(cost.webSearchCost, "USD"))],
    ["Total Cost", metricValue(formatCurrencyValue(cost.usd, "USD"))],
  ];
}

function ResponseMetricsComparison({
  originalAgent,
  testAgent,
  formatNumberValue,
  formatCurrencyValue,
}) {
  const [hoveredMetric, setHoveredMetric] = useState(null);
  const originalMetrics = getResponseMetrics(
    originalAgent,
    formatNumberValue,
    formatCurrencyValue
  );
  const testMetrics = getResponseMetrics(
    testAgent,
    formatNumberValue,
    formatCurrencyValue
  );
  const metricTestId = (label) =>
    label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const metricGroups = Array.from(
    { length: Math.ceil(originalMetrics.length / 3) },
    (_, groupIndex) => originalMetrics.slice(groupIndex * 3, groupIndex * 3 + 3)
  );

  return (
    <Box
      data-testid="response-metrics-comparison"
      sx={{ gridColumn: "1 / -1", display: "grid", gap: 0.5 }}
    >
      {metricGroups.map((group, groupIndex) => (
        <Box
          key={group.map(([label]) => label).join("-")}
          data-testid={`response-metric-group-${groupIndex}`}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            gap: 1,
            alignItems: "stretch",
          }}
        >
          {["original", "test"].map((side) => (
            <Box
              key={side}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
                gap: 0.5,
                alignItems: "stretch",
              }}
            >
              {group.map(([label, originalValue], itemIndex) => {
                const metricIndex = groupIndex * 3 + itemIndex;
                const value = side === "original"
                  ? originalValue
                  : testMetrics[metricIndex]?.[1] ?? "N/A";
                const testId = metricTestId(label);
                const isHighlighted = hoveredMetric === label;

                return (
                  <Box
                    key={label}
                    data-testid={`${side}-response-metric-${testId}`}
                    aria-label={`${side} ${label}`}
                    onMouseEnter={() => setHoveredMetric(label)}
                    onMouseLeave={() => setHoveredMetric(null)}
                    sx={{
                      minWidth: 0,
                      minHeight: 48,
                      height: "100%",
                      px: 0.9,
                      py: 0.55,
                      border: "1px solid",
                      borderColor: isHighlighted
                        ? "rgba(249, 115, 22, 0.38)"
                        : "rgba(15, 23, 42, 0.08)",
                      borderRadius: "9px",
                      backgroundColor: isHighlighted
                        ? "rgba(255, 237, 213, 0.82)"
                        : side === "test"
                          ? "rgba(255, 247, 241, 0.72)"
                          : "rgba(248, 250, 252, 0.82)",
                      boxShadow: isHighlighted
                        ? "0 8px 20px rgba(249, 115, 22, 0.10)"
                        : "none",
                      transition: "background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ fontSize: "0.66rem", lineHeight: 1.2 }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={label === "Total Cost" ? 800 : 650}
                      sx={{
                        overflowWrap: "anywhere",
                        color: label === "Total Cost" ? "primary.main" : "inherit",
                        fontSize: "0.76rem",
                        lineHeight: 1.3,
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

// Function to calculate cost based on model and tokens
function calculateCost(model, promptTokens, completionTokens, reasoningTokens = 0, searchCallCount = 0) {
  const normalizedPromptTokens = promptTokens || 0;
  const normalizedCompletionTokens = completionTokens || 0;
  const normalizedReasoningTokens = reasoningTokens || 0;
  const webSearchCost = calculateWebSearchCost(searchCallCount);

  if (!model || (normalizedPromptTokens === 0 && normalizedCompletionTokens === 0 && normalizedReasoningTokens === 0 && webSearchCost === 0)) {
    return { usd: 0, inputCost: 0, outputCost: 0, reasoningCost: 0, webSearchCost: 0 };
  }

  // Normalize model name and find exact match first
  const normalizedModel = model.toLowerCase().replace(/_/g, '-');
  let pricing = null;

  // Try exact match first
  if (OPENAI_PRICING[normalizedModel]) {
    pricing = OPENAI_PRICING[normalizedModel];
  } else {
    // Try partial matching for backwards compatibility
    for (const [key, value] of Object.entries(OPENAI_PRICING)) {
      if (normalizedModel.includes(key)) {
        pricing = value;
        break;
      }
    }
  }

  // Default to gpt-4o pricing if model not found
  if (!pricing) {
    pricing = OPENAI_PRICING["gpt-4o"];
  }

  const inputCost = (normalizedPromptTokens / 1000000) * pricing.input;
  const outputCost = (normalizedCompletionTokens / 1000000) * pricing.output;
  const reasoningPrice = getReasoningTokenPrice(normalizedModel, pricing);
  const reasoningCost = (normalizedReasoningTokens / 1000000) * reasoningPrice;
  const totalUsd = inputCost + outputCost + reasoningCost + webSearchCost;

  return {
    usd: totalUsd,
    inputCost,
    outputCost,
    reasoningCost,
    webSearchCost
  };
}

const LogAgents = ({ initialTraceId = "" }) => {
  const isAdmin = isAdminUser(getUserEmail());
  const [traceId, setTraceId] = useState(initialTraceId);
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentEdits, setAgentEdits] = useState(null);
  const [testPromptResult, setTestPromptResult] = useState(null);
  const [testPromptTraceId, setTestPromptTraceId] = useState("");
  const [testPromptTraceAgents, setTestPromptTraceAgents] = useState([]);
  const [testPromptTraceError, setTestPromptTraceError] = useState("");
  const [testPromptLoading, setTestPromptLoading] = useState(false);
  const [testPromptModelId, setTestPromptModelId] = useState(DEFAULT_TEST_PROMPT_MODEL);
  const [testPromptReasoningEffortLevel, setTestPromptReasoningEffortLevel] = useState(DEFAULT_REASONING_EFFORT_LEVEL);
  const [testPromptWebSearch, setTestPromptWebSearch] = useState("false");
  const [testPromptAllowedDomains, setTestPromptAllowedDomains] = useState("");
  const [showAgentTraceData, setShowAgentTraceData] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerAgent, setDrawerAgent] = useState(null);
  const [argsExpanded, setArgsExpanded] = useState(false);
  const [argsFormatAsJson, setArgsFormatAsJson] = useState(false);
  const [formatResponseText, setFormatResponseText] = useState(false);
  const [formatTestResponseText, setFormatTestResponseText] = useState(false);
  const [agentFilterText, setAgentFilterText] = useState("");
  const [dialogMaximized, setDialogMaximized] = useState(false);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  const [aiAnalysisDialogOpen, setAiAnalysisDialogOpen] = useState(false);
  const [traceLogsLoading, setTraceLogsLoading] = useState(false);
  const [traceLogsData, setTraceLogsData] = useState([]);
  const [traceLogsDialogOpen, setTraceLogsDialogOpen] = useState(false);
  const [traceLogsFilter, setTraceLogsFilter] = useState("");
  const [traceLogsDebouncedFilter, setTraceLogsDebouncedFilter] = useState("");
  const [traceLogsTypeFilter, setTraceLogsTypeFilter] = useState("All");
  const [curlDialogOpen, setCurlDialogOpen] = useState(false);
  const [curlCommand, setCurlCommand] = useState("");
  const [curlReplayLoading, setCurlReplayLoading] = useState(false);
  const [curlReplayResult, setCurlReplayResult] = useState(null);
  const [curlReplaySuccess, setCurlReplaySuccess] = useState(false);
  const [curlReplayError, setCurlReplayError] = useState("");
  const [curlReplayUrl, setCurlReplayUrl] = useState("");
  const [curlReplayTab, setCurlReplayTab] = useState(0);
  const [curlReplayHeaders, setCurlReplayHeaders] = useState([]);
  const [curlReplayBody, setCurlReplayBody] = useState("");
  const [curlReplayMethod, setCurlReplayMethod] = useState("POST");
  const [agentTraceDataFilter, setAgentTraceDataFilter] = useState("");
  const shouldShowReasoningEffortLevel = isReasoningModel(testPromptModelId);
  const shouldShowWebSearchControls = isReasoningModel(testPromptModelId);
  const reasoningEffortOptions = getReasoningEffortOptions(testPromptModelId);
  const shouldShowAllowedDomainsInput =
    shouldShowWebSearchControls && testPromptWebSearch === "true";

  const testPromptModelOptions = useMemo(() => {
    const currentModel = selectedAgent?.promptContent?.llmModel;
    return Array.from(
      new Set([
        currentModel,
        testPromptModelId,
        ...Object.keys(OPENAI_PRICING),
      ].filter(Boolean))
    );
  }, [selectedAgent, testPromptModelId]);

  // Sync with initialTraceId prop if it changes
  useEffect(() => {
    if (initialTraceId) {
      setTraceId(initialTraceId);
    }
  }, [initialTraceId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setTraceLogsDebouncedFilter(traceLogsFilter);
    }, 200);
    return () => clearTimeout(timeout);
  }, [traceLogsFilter]);

  // Auto-fetch effect with debounce
  useEffect(() => {
    if (!traceId.trim()) {
      setAgentData(null);
      setError("");
      return;
    }

    const timeoutId = setTimeout(() => {
      handleFetchAgents();
    }, 500); // 0.5 seconds delay

    return () => clearTimeout(timeoutId);
  }, [traceId]);

  const handleFetchAgents = async () => {
    if (!traceId.trim()) {
      setError("Please enter a valid Trace ID.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get(`/agents/trace/${traceId}`);
      if (response.data?.data) {
        setAgentData(response.data.data);
      } else {
        setError("No agent trace found for provided Trace ID.");
        setAgentData(null);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch agent trace. Please try again later.");
      setAgentData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setTraceId(text); // This will trigger the useEffect to fetch data
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
      setError("Failed to paste from clipboard. Please check browser permissions.");
    }
  };

  const totalExecutionTime = () => {
    if (!agentData || agentData.length === 0) {
      return 0;
    }
    const startTimes = agentData.map(a => new Date(a["@timestamp"]).getTime());
    const endTimes = agentData.map(a => new Date(a["@timestamp"]).getTime() + (a.durationMs || 0));
    const minStart = Math.min(...startTimes);
    const maxEnd = Math.max(...endTimes);
    return maxEnd - minStart;
  };

  const getCostBreakdown = () => {
    if (!agentData || agentData.length === 0) {
      return {
        totalCost: { usd: 0 },
        byModel: {},
        byTokenType: { input: 0, output: 0, reasoning: 0, webSearch: 0 }
      };
    }

    let totalUsd = 0;
    let totalInputCost = 0;
    let totalOutputCost = 0;
    let totalReasoningCost = 0;
    let totalWebSearchCost = 0;
    const byModel = {};

    agentData.forEach(agent => {
      const model = agent.promptContent?.llmModel || 'unknown';
      const promptTokens = agent.promptContent?.promptTokensFromResponse || 0;
      const completionTokens = agent.promptContent?.completionTokensFromResponse || 0;
      const reasoningTokens = agent.promptContent?.reasoningTokensFromResponse || 0;
      const searchCallCount = getSearchCallCount(agent.promptContent);

      // Calculate cost when the call has token usage or billable web searches.
      if (promptTokens > 0 || completionTokens > 0 || reasoningTokens > 0 || searchCallCount > 0) {
        const cost = calculateCost(
          model,
          promptTokens,
          completionTokens,
          reasoningTokens,
          searchCallCount
        );

        totalUsd += cost.usd;
        totalInputCost += cost.inputCost;
        totalOutputCost += cost.outputCost;
        totalReasoningCost += cost.reasoningCost;
        totalWebSearchCost += cost.webSearchCost;

        if (!byModel[model]) {
          byModel[model] = {
            count: 0,
            totalTokens: { input: 0, output: 0, reasoning: 0 },
            totalSearchCalls: 0,
            totalCost: { usd: 0, input: 0, output: 0, reasoning: 0, webSearch: 0 }
          };
        }

        byModel[model].count += 1;
        byModel[model].totalTokens.input += promptTokens;
        byModel[model].totalTokens.output += completionTokens;
        byModel[model].totalTokens.reasoning += reasoningTokens;
        byModel[model].totalSearchCalls += searchCallCount;
        byModel[model].totalCost.usd += cost.usd;
        byModel[model].totalCost.input += cost.inputCost;
        byModel[model].totalCost.output += cost.outputCost;
        byModel[model].totalCost.reasoning += cost.reasoningCost;
        byModel[model].totalCost.webSearch += cost.webSearchCost;
      }
    });

    return {
      totalCost: { usd: totalUsd },
      byModel,
      byTokenType: {
        input: totalInputCost,
        output: totalOutputCost,
        reasoning: totalReasoningCost,
        webSearch: totalWebSearchCost,
      }
    };
  };

  const formatExecutionTime = (ms) =>
    ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;

  const formatTimelineDuration = (ms) => `${((ms || 0) / 1000).toFixed(2)}s`;

  const formatTimelineTick = (ms) =>
    `${Number(((ms || 0) / 1000).toFixed(2))}s`;

  const formatCurrency = (amount, currency, precision = 6) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    }).format(amount);
  };

  const formatNumber = (num, precision = 0) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    }).format(num);
  };

  const getTimelineEntries = () => {
    if (!agentData?.length) return [];
    let data = agentData;
    if (agentFilterText.trim()) {
      const needle = agentFilterText.toLowerCase();
      data = agentData.filter((a) => {
        const req = (a.promptContent?.llmRequestFormated || "").toLowerCase();
        const res = (a.promptContent?.llmResponseFormated || "").toLowerCase();
        const variants = JSON.stringify(a.promptContent?.arguments?.variantsData || "").toLowerCase();
        return req.includes(needle) || res.includes(needle) || variants.includes(needle);
      });
    }
    if (!data.length) return [];
    const referenceTime = Math.min(
      ...data.map((a) => new Date(a["@timestamp"]).getTime())
    );
    const sorted = [...data].sort(
      (a, b) => new Date(a["@timestamp"]) - new Date(b["@timestamp"])
    );
    return sorted.map((agent) => {
      const startOffsetMs = new Date(agent["@timestamp"]).getTime() - referenceTime;
      const durationMs = agent.durationMs || 0;

      return {
        agent,
        startOffsetMs,
        durationMs,
        endOffsetMs: startOffsetMs + durationMs,
      };
    });
  };

  const filteredAgentTraceData = useMemo(() => {
    if (!Array.isArray(agentData)) {
      return [];
    }

    const needle = agentTraceDataFilter.trim().toLowerCase();
    if (!needle) {
      return agentData;
    }

    const matchingItems = agentData.filter((item) =>
      JSON.stringify(item).toLowerCase().includes(needle)
    );

    return matchingItems.sort((leftItem, rightItem) => {
      const leftAgentName = String(leftItem?.agentName || "").toLowerCase();
      const rightAgentName = String(rightItem?.agentName || "").toLowerCase();
      const leftMatchesAgentName = leftAgentName.includes(needle);
      const rightMatchesAgentName = rightAgentName.includes(needle);

      if (leftMatchesAgentName && !rightMatchesAgentName) {
        return -1;
      }

      if (!leftMatchesAgentName && rightMatchesAgentName) {
        return 1;
      }

      return 0;
    });
  }, [agentData, agentTraceDataFilter]);

  const highlightedAgentTraceJson = useMemo(() => {
    const formattedJson = JSON.stringify(filteredAgentTraceData, null, 2);
    const needle = agentTraceDataFilter.trim();

    if (!needle) {
      return formattedJson;
    }

    const regex = new RegExp(`(${escapeRegExp(needle)})`, "gi");
    const parts = formattedJson.split(regex);

    return parts.map((part, index) => {
      const isMatch = part.toLowerCase() === needle.toLowerCase();
      if (!isMatch) {
        return part;
      }

      return (
        <Box
          key={`${part}-${index}`}
          component="span"
          sx={{
            backgroundColor: "rgba(255, 193, 7, 0.35)",
            color: "#7c2d12",
            borderRadius: "4px",
            px: 0.25,
            fontWeight: 700,
          }}
        >
          {part}
        </Box>
      );
    });
  }, [filteredAgentTraceData, agentTraceDataFilter]);

  const timelineEntries = getTimelineEntries();
  const timelineMaxMs = Math.max(
    1,
    ...timelineEntries.map(({ endOffsetMs }) => endOffsetMs)
  );
  const timelineTicks = Array.from({ length: 5 }, (_, index) => ({
    position: index * 25,
    label: formatTimelineTick((timelineMaxMs * index) / 4),
  }));
  const timelineLegend = Array.from(
    new Set(timelineEntries.map(({ agent }) => agent.agentName))
  );

  const handleAiAnalyse = async () => {
    if (!traceId.trim()) return;
    setAiAnalysisLoading(true);
    setAiAnalysisResult(null);
    try {
      const response = await axiosInstance.get(`/agents/analyser/${traceId}`);
      let analysis = response.data?.data?.analysis || "No analysis available.";
      if (typeof analysis === "string") {
        analysis = analysis.replace(/\\"/g, '"').replace(/\\n/g, '\n');
      }
      setAiAnalysisResult(analysis);
      setAiAnalysisDialogOpen(true);
    } catch (err) {
      console.error("Error fetching AI analysis:", err);
      setAiAnalysisResult("Failed to fetch AI analysis. Please try again.");
      setAiAnalysisDialogOpen(true);
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const handleTraceLogs = async () => {
    if (!traceId.trim()) return;
    setTraceLogsLoading(true);
    setTraceLogsData([]);
    try {
      const response = await axiosInstance.get(`/trace/${traceId}`);
      setTraceLogsData(response.data?.data || []);
      setTraceLogsFilter("");
      setTraceLogsTypeFilter("All");
      setTraceLogsDialogOpen(true);
    } catch (err) {
      console.error("Error fetching trace logs:", err);
      setTraceLogsData([]);
      setTraceLogsDialogOpen(true);
    } finally {
      setTraceLogsLoading(false);
    }
  };

  const handleOpenCurlReplay = (logIndex) => {
    const { startingLog, headersLog, bodyLog } = extractRequestGroup(traceLogsData, logIndex);
    const curl = buildCurlCommand(startingLog, headersLog, bodyLog);
    setCurlCommand(curl);
    const urlMatch = curl.match(/'(https?:\/\/[^']+)'/);
    setCurlReplayUrl(urlMatch?.[1]?.replace(/^http:/, "https:") || "");
    const methodMatch = curl.match(/curl -X (\w+)/);
    setCurlReplayMethod(methodMatch?.[1] || "POST");
    const headerMatches = [...curl.matchAll(/-H '([^:]+):\s*([^']+)'/g)];
    setCurlReplayHeaders(headerMatches.map(([, key, value]) => ({ key, value })));
    const bodyMatch = curl.match(/-d '([^']+)'/);
    let parsedBody = bodyMatch?.[1] || "";
    try { parsedBody = JSON.stringify(JSON.parse(parsedBody), null, 2); } catch {}
    setCurlReplayBody(parsedBody);
    setCurlReplayTab(0);
    setCurlReplayResult(null);
    setCurlReplayError("");
    setCurlDialogOpen(true);
  };

  const handleExecuteReplay = async () => {
    setCurlReplayLoading(true);
    setCurlReplayResult(null);
    setCurlReplayError("");
    try {
      let method, url, body, headers;

      if (curlReplayTab === 0) {
        method = curlReplayMethod;
        url = curlReplayUrl;
        body = curlReplayBody;
        headers = {};
        curlReplayHeaders.forEach(({ key, value }) => { if (key.trim()) headers[key] = value; });
      } else {
        const methodMatch = curlCommand.match(/curl -X (\w+)/);
        const bodyMatch = curlCommand.match(/-d '([^']+)'/);
        method = methodMatch?.[1] || "POST";
        url = curlReplayUrl || "";
        body = bodyMatch?.[1] || "";
        const headerMatches = [...curlCommand.matchAll(/-H '([^:]+):\s*([^']+)'/g)];
        headers = {};
        headerMatches.forEach(([, key, value]) => { headers[key] = value; });
      }

      const response = await fetch(url, {
        method,
        headers,
        body: method !== "GET" ? body : undefined,
      });

      const contentType = response.headers.get("content-type") || "";
      let result;
      if (contentType.includes("application/json")) {
        result = JSON.stringify(await response.json(), null, 2);
      } else {
        result = await response.text();
      }
      let finalResult = `Status: ${response.status} ${response.statusText}\n\n${result}`;

      // Detect streamed chatStream chunks and concatenate the full message
      const chatStreamMatches = [...result.matchAll(/data:\s*\{"chatStream":"([^"]*)"\}/g)];
      if (chatStreamMatches.length > 0) {
        const fullMessage = chatStreamMatches.map(m => m[1]).join("");
        finalResult += `\n\n--- Full ChatStream Message ---\n${fullMessage}`;
      }

      setCurlReplayResult(finalResult);
      setCurlReplaySuccess(response.ok);
    } catch (err) {
      setCurlReplayError(err.message || "Request failed");
      setCurlReplaySuccess(false);
    } finally {
      setCurlReplayLoading(false);
    }
  };

  const getOriginalLogIndex = (log) => {
    return traceLogsData.indexOf(log);
  };

  const isRequestBodyLog = (log) => {
    return log.message?.startsWith("Request Body:");
  };

  const hasCompleteRequestGroup = (log) => {
    if (!isRequestBodyLog(log)) return false;
    const idx = getOriginalLogIndex(log);
    if (idx < 0) return false;
    const { startingLog, headersLog } = extractRequestGroup(traceLogsData, idx);
    return !!(startingLog && headersLog);
  };

  // derive summary
  const userPrompt = agentData?.find(
    (a) => a.agentName === "getIntentSummary"
  )?.promptContent.arguments.userPrompt;

  const tenantInfo = agentData?.find(
    (a) => a.tenantId || a.tenantName
  );

  const summaryEntries =
    agentData?.map((a) => ({
      name: a.agentName,
      response: a.promptContent.llmResponseFormated,
      llmRequestFormated: a.promptContent.llmRequestFormated,
      variantsData: a.promptContent?.arguments?.variantsData,
    })) || [];

  const filteredSummaryEntries = useMemo(() => {
    if (!agentFilterText.trim()) return summaryEntries;
    const needle = agentFilterText.toLowerCase();
    return summaryEntries.filter((entry) => {
      const req = (entry.llmRequestFormated || "").toLowerCase();
      const res = (entry.response || "").toLowerCase();
      const variants = JSON.stringify(entry.variantsData || "").toLowerCase();
      return req.includes(needle) || res.includes(needle) || variants.includes(needle);
    });
  }, [summaryEntries, agentFilterText]);

  const handleCloseAgentDialog = () => {
    setSelectedAgent(null);
    setTestPromptResult(null);
    setTestPromptTraceId("");
    setTestPromptTraceAgents([]);
    setTestPromptTraceError("");
    setArgsExpanded(false);
    setArgsFormatAsJson(false);
    setFormatResponseText(false);
    setFormatTestResponseText(false);
    setDialogMaximized(false);
  };

  const handleTestPrompt = async () => {
    setTestPromptLoading(true);
    setTestPromptTraceId("");
    setTestPromptTraceAgents([]);
    setTestPromptTraceError("");
    setFormatTestResponseText(true);
    try {
      var payload = {
        prompt: agentEdits.llmRequestFormated,
        llmModel: testPromptModelId || selectedAgent.promptContent.llmModel || DEFAULT_TEST_PROMPT_MODEL,
        temperature: selectedAgent.promptContent.temperature,
        topP: selectedAgent.promptContent.topP,
        maxTokens: selectedAgent.promptContent.maxTokens == 0 ? 10000 : selectedAgent.promptContent.maxTokens,
        kernelContextId: selectedAgent.kernelContextId,
        agentName: selectedAgent.agentName,
        responseFormat: selectedAgent.promptContent.responseFormat,
        placeHolders: selectedAgent.promptContent.tenantPromptPlaceHolders,
        draft: selectedAgent.promptContent.draft,
        tenantId: selectedAgent?.tenantId || agentData?.find(a => a.tenantId)?.tenantId || "",
      }

      if (isReasoningModel(payload.llmModel)) {
        payload.ReasoningEffortLevel = normalizeReasoningEffort(payload.llmModel, testPromptReasoningEffortLevel);
        payload.webSearch = testPromptWebSearch === "true";

        if (testPromptWebSearch === "true") {
          payload.allowedDomains = testPromptAllowedDomains.trim();
        }
      }

      const currentTenantId = selectedAgent?.tenantId || agentData?.find(a => a.tenantId)?.tenantId || "";
      const currentKernelContextId = selectedAgent?.kernelContextId || "";

      const res = await axiosInstance.post(`/prompt/test-prompt`, payload, {
        headers: {
          tenantId: currentTenantId,
          kernelContextId: currentKernelContextId
        }
      });

      const immediateTestResult =
        typeof res.data === "string"
          ? res.data
          : JSON.stringify(res.data, null, 2);
      setTestPromptResult(immediateTestResult);

      const traceIdFromHeader =
        res.headers?.["trace-id"] ||
        res.headers?.["Trace-Id"] ||
        res.headers?.["traceid"] ||
        "";

      if (!traceIdFromHeader) {
        setTestPromptTraceError("Trace-Id header was not returned by test-prompt.");
        return;
      }

      setTestPromptTraceId(traceIdFromHeader);

      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const traceRes = await axiosInstance.get(`/agents/trace/${traceIdFromHeader}`);
        const tracedAgents = Array.isArray(traceRes.data?.data) ? traceRes.data.data : [];
        setTestPromptTraceAgents(tracedAgents);
        const tracedResponseAgent =
          tracedAgents.find(
            (agent) =>
              agent.agentName === selectedAgent?.agentName &&
              agent.promptContent?.llmResponseFormated
          ) ||
          tracedAgents.find((agent) => agent.promptContent?.llmResponseFormated);
        const tracedResponse = tracedResponseAgent?.promptContent?.llmResponseFormated || "";
        if (tracedResponse) {
          setFormatTestResponseText(true);
        }

        if (!tracedAgents.length) {
          setTestPromptTraceError(`No agent information returned for Trace ID ${traceIdFromHeader}.`);
        }
      } catch (traceErr) {
        setTestPromptTraceError(`Failed to fetch agent information for Trace ID ${traceIdFromHeader}: ${traceErr.message}`);
      }
    } catch (err) {
      setTestPromptResult(`Error: ${err.message}`);
    } finally {
      setTestPromptLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAgent?.promptContent) {
      const promptContent = selectedAgent.promptContent;
      const placeholders = promptContent.tenantPromptPlaceHolders || [];
      const initialModel = promptContent.llmModel || DEFAULT_TEST_PROMPT_MODEL;
      const initialReasoningEffortLevel =
        promptContent.ReasoningEffortLevel ||
        promptContent.reasoningEffortLevel ||
        promptContent.reasoningEffort ||
        DEFAULT_REASONING_EFFORT_LEVEL;
      const placeholderWebSearch = getPlaceholderValue(
        placeholders,
        "yaml-execution-settings:web_search"
      );
      const tracedWebSearch = promptContent.webSearch ?? promptContent.WebSearch;
      const initialWebSearch = tracedWebSearch ?? placeholderWebSearch;
      const placeholderAllowedDomains = getPlaceholderValue(
        placeholders,
        "yaml-execution-settings:allowed_domains"
      );

      setTestPromptModelId(initialModel);
      setTestPromptReasoningEffortLevel(normalizeReasoningEffort(initialModel, initialReasoningEffortLevel));
      setTestPromptWebSearch(normalizeBooleanLikeValue(initialWebSearch) ? "true" : "false");
      setTestPromptAllowedDomains(
        Array.isArray(placeholderAllowedDomains)
          ? placeholderAllowedDomains.join(", ")
          : (placeholderAllowedDomains || "")
      );
      const initialResponse =
        typeof promptContent.llmResponseFormated === "string"
          ? promptContent.llmResponseFormated.trimEnd()
          : promptContent.llmResponseFormated || "";
      setArgsExpanded(false);
      setArgsFormatAsJson(false);
      setFormatTestResponseText(false);
      setFormatResponseText(true);
      setAgentEdits({
        rawPrompt: promptContent.rawPrompt || "",
        rawPromptWithPlaceHolder:
          promptContent.rawPromptWithPlaceHolder || "",
        llmRequestFormated:
          promptContent.llmRequestFormated || "",
        llmResponseFormated: initialResponse,
        arguments:
          typeof promptContent.arguments === "string"
            ? promptContent.arguments
            : JSON.stringify(promptContent.arguments || {}, null, 2),
      });
    } else {
      setAgentEdits(null);
      setTestPromptReasoningEffortLevel(DEFAULT_REASONING_EFFORT_LEVEL);
      setTestPromptWebSearch("false");
      setTestPromptAllowedDomains("");
    }
  }, [selectedAgent]);

  const handleUpdateLlmRequestFormated = (newVal) =>
    setAgentEdits((p) => ({ ...p, llmRequestFormated: newVal }));

  const handleOpenDrawer = (agent) => {
    setDrawerAgent(agent);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setDrawerAgent(null);
  };

  const costBreakdown = getCostBreakdown();

  const chatMessages = useMemo(() => {
    if (!agentData) {
      return [];
    }

    const agentWithHistory = agentData.find(
      (agent) =>
        agent.promptContent?.arguments?.chatHistory ||
        agent.promptContent?.arguments?.conversationHistory ||
        agent.promptContent?.arguments?.messages ||
        agent.promptContent?.chatHistory ||
        agent.chatHistory
    );

    if (!agentWithHistory) {
      return [];
    }

    let chatHistory = "";
    try {
      const rawHistory =
        agentWithHistory.promptContent?.arguments?.chatHistory ||
        agentWithHistory.promptContent?.arguments?.conversationHistory ||
        agentWithHistory.promptContent?.arguments?.messages ||
        agentWithHistory.promptContent?.chatHistory ||
        agentWithHistory.chatHistory;
      chatHistory = typeof rawHistory === "string" ? JSON.parse(rawHistory) : rawHistory;
    } catch (e) {
      chatHistory =
        agentWithHistory.promptContent?.arguments?.chatHistory ||
        agentWithHistory.promptContent?.arguments?.conversationHistory ||
        agentWithHistory.promptContent?.arguments?.messages ||
        agentWithHistory.promptContent?.chatHistory ||
        agentWithHistory.chatHistory;
    }

    const messages = [];

    if (Array.isArray(chatHistory)) {
      chatHistory.forEach((msg) => {
        const role = (msg.role || msg.type || "").toLowerCase();
        const text = msg.content || msg.text || msg.message || "";
        if (role === "user" || role === "human") {
          messages.push({ type: "user", text });
        } else if (role === "assistant" || role === "ai" || role === "bot") {
          messages.push({ type: "assistant", text });
        }
      });
    } else if (chatHistory && typeof chatHistory === "string") {
      const parts = chatHistory.split(/(?=(?:Assistant:|User:))/);
      parts.forEach((part) => {
        const trimmed = part.trim();
        if (trimmed.startsWith("Assistant:")) {
          messages.push({
            type: "assistant",
            text: trimmed.replace("Assistant:", "").trim(),
          });
        } else if (trimmed.startsWith("User:")) {
          messages.push({
            type: "user",
            text: trimmed.replace("User:", "").trim(),
          });
        }
      });
    }

    if (userPrompt) {
      let currentPrompt = "";
      try {
        currentPrompt =
          typeof userPrompt === "string" ? JSON.parse(userPrompt) : userPrompt;
      } catch (e) {
        currentPrompt = userPrompt;
      }

      const lastUserMessage = messages.filter((m) => m.type === "user").pop();
      const isDifferentFromLast =
        !lastUserMessage || lastUserMessage.text !== currentPrompt;

      if (isDifferentFromLast && currentPrompt.trim()) {
        messages.push({
          type: "user",
          text: currentPrompt,
        });
      }
    }

    const responseAgent = agentData.find(
      (agent) => agent.agentName.includes("Response")
    );

    if (responseAgent && responseAgent.promptContent?.llmResponseFormated) {
      messages.push({
        type: "assistant",
        text: responseAgent.promptContent.llmResponseFormated,
      });
    }

    return messages.filter((m) => m.text); // Return only messages with content
  }, [agentData, userPrompt]);

  const sectionPaperSx = {
    p: { xs: 2, md: 2.5 },
    mt: 3,
    borderRadius: "22px",
    border: "1px solid rgba(255, 132, 76, 0.10)",
    boxShadow: "0 24px 60px rgba(236, 123, 55, 0.08)",
    backgroundColor: "rgba(255,255,255,0.92)",
  };

  const softMetricPaperSx = {
    p: { xs: 2, md: 2.5 },
    mb: 3,
    borderRadius: "22px",
    border: "1px solid rgba(255, 132, 76, 0.10)",
    background: "linear-gradient(180deg, rgba(255, 244, 238, 0.94), rgba(255, 251, 247, 0.98))",
    boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)",
  };

  const controlButtonSx = {
    minHeight: 56,
    borderRadius: "16px",
    textTransform: "none",
    fontWeight: 700,
    color: "#fff",
    background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
    boxShadow: "0 14px 30px rgba(255, 139, 71, 0.28)",
    "&:hover": {
      background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)",
      boxShadow: "0 18px 36px rgba(255, 139, 71, 0.34)",
    },
  };

  const promptArgumentsDisplay = argsFormatAsJson
    ? formatJsonForDisplay(agentEdits?.arguments || "")
    : agentEdits?.arguments || "";
  const promptArgumentsJson = getJsonSyntaxDisplayValue(agentEdits?.arguments || "");
  const llmRequestFields = getLlmRequestFields(
    agentEdits?.llmRequestFormated || ""
  );
  const testPromptResponseAgent =
    testPromptTraceAgents.find(
      (agent) =>
        agent.agentName === selectedAgent?.agentName &&
        agent.promptContent?.llmResponseFormated
    ) ||
    testPromptTraceAgents.find(
      (agent) => agent.promptContent?.llmResponseFormated
    ) ||
    null;
  const testPromptResponseValue =
    testPromptResponseAgent?.promptContent?.llmResponseFormated ||
    testPromptResult ||
    "";

  const testPromptCostComparison = (() => {
    if (!testPromptResult) return null;

    if (!testPromptTraceAgents.length) {
      return {
        text: testPromptTraceError
          ? "Cost comparison unavailable because test trace details were not returned."
          : "Calculating cost comparison…",
        color: "#6b7280",
        backgroundColor: "rgba(248, 250, 252, 0.92)",
      };
    }

    const originalCost = calculateCost(
      selectedAgent.promptContent?.llmModel,
      selectedAgent.promptContent?.promptTokensFromResponse,
      selectedAgent.promptContent?.completionTokensFromResponse,
      selectedAgent.promptContent?.reasoningTokensFromResponse,
      getSearchCallCount(selectedAgent.promptContent)
    ).usd;
    const testCost = testPromptTraceAgents.reduce((total, agent) => {
      const promptContent = agent.promptContent || {};
      return total + calculateCost(
        promptContent.llmModel,
        promptContent.promptTokensFromResponse,
        promptContent.completionTokensFromResponse,
        promptContent.reasoningTokensFromResponse,
        getSearchCallCount(promptContent)
      ).usd;
    }, 0);
    const costTransition = `${formatCurrency(originalCost, "USD")} → ${formatCurrency(testCost, "USD")}`;
    const formatMultiplier = (value) =>
      value.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");

    if (originalCost === testCost) {
      return {
        text: `1x — same cost (${formatCurrency(originalCost, "USD")})`,
        color: "#475569",
        backgroundColor: "rgba(241, 245, 249, 0.94)",
      };
    }

    if (originalCost === 0) {
      return {
        text: `Test cost increased from zero (${costTransition})`,
        color: "#b91c1c",
        backgroundColor: "rgba(254, 226, 226, 0.72)",
      };
    }

    if (testCost === 0) {
      return {
        text: `Test was 0x the original cost — 100% cheaper (${costTransition})`,
        color: "#15803d",
        backgroundColor: "rgba(220, 252, 231, 0.76)",
      };
    }

    const ratio = testCost / originalCost;
    if (ratio > 1) {
      return {
        text: `Test was ${formatMultiplier(ratio)}x more expensive (${costTransition})`,
        color: "#b91c1c",
        backgroundColor: "rgba(254, 226, 226, 0.72)",
      };
    }

    return {
      text: `Test was ${formatMultiplier(1 / ratio)}x cheaper (${costTransition})`,
      color: "#15803d",
      backgroundColor: "rgba(220, 252, 231, 0.76)",
    };
  })();


  return (
    <Box
      sx={{
        maxWidth: 1400,
        mx: "auto",
        px: { xs: 1, md: 2 },
        py: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          borderRadius: { xs: 4, md: 6 },
          background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.28), rgba(255,255,255,0.94) 42%), linear-gradient(180deg, rgba(255, 248, 243, 0.96), rgba(255, 255, 255, 0.92))",
          boxShadow: "0 24px 60px rgba(236, 123, 55, 0.10)",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2.25,
            mb: 3.5,
          }}
        >
          <Box
            sx={{
              width: { xs: 56, md: 64 },
              height: { xs: 56, md: 64 },
              borderRadius: "20px",
              background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
              boxShadow: "0 18px 38px rgba(255, 139, 71, 0.30)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <AccountTreeOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "2rem", md: "2.55rem" },
                lineHeight: 1.05,
                fontWeight: 800,
                color: "#202531",
                letterSpacing: "-0.03em",
                mb: 0.8,
              }}
            >
              Agents Trace
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#6b7280",
                fontWeight: 500,
              }}
            >
              Inspect execution flow, timing, token usage, and trace-level agent details.
            </Typography>
          </Box>
        </Box>

      {agentData && (
        <Paper
          elevation={0}
          sx={softMetricPaperSx}
        >
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={6} lg={3}>
              <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '0.9rem', color: "#f97316" }}>
                Agents Executed: {agentData.length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '0.9rem', color: "#f97316" }}>
                Total Execution Time: {formatExecutionTime(totalExecutionTime())}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '0.9rem', color: "#f97316" }}>
                Total Cost (USD): {formatCurrency(costBreakdown.totalCost.usd, 'USD')}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper
        elevation={0}
        sx={{
          mb: 3.25,
          p: { xs: 2, md: 3 },
          borderRadius: "24px",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          backgroundColor: "rgba(255,255,255,0.88)",
          boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)",
        }}
      >
        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} md={9}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "#6b7280",
                fontWeight: 700,
                mb: 0.9,
                px: 0.5,
              }}
            >
              Trace ID
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Trace ID (auto-filters after 0.5s)"
              value={traceId}
              onChange={(e) => setTraceId(e.target.value)}
              InputProps={{
                endAdornment: loading && <CircularProgress size={16} sx={{ color: "#f97316" }} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: 56,
                  borderRadius: "16px",
                  backgroundColor: "#fff",
                  fontWeight: 600,
                  color: "#202531",
                  "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                  "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                  "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={handlePasteFromClipboard}
                aria-label="paste trace id"
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  backgroundColor: "#fff",
                  boxShadow: "0 14px 30px rgba(236, 123, 55, 0.08)",
                  color: "#4b5563",
                  "&:hover": {
                    backgroundColor: "#fff7f1",
                    color: "#f97316",
                  },
                }}
              >
                <ContentPasteIcon />
              </IconButton>
              <Button
                fullWidth
                variant="contained"
                onClick={handleFetchAgents}
                disabled={loading || !traceId.trim()}
                sx={controlButtonSx}
              >
                {loading ? <CircularProgress size={16} color="inherit" /> : "Manual Fetch"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {agentData && (
        <Paper elevation={0} sx={sectionPaperSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ m: 0 }}>
              Execution Timeline (Relative Seconds)
            </Typography>
            <TextField
              size="small"
              placeholder="Filter by request/response content (e.g. productID)"
              value={agentFilterText}
              onChange={(e) => setAgentFilterText(e.target.value)}
              sx={{
                width: 360,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#fffaf7',
                  '& fieldset': { borderColor: 'rgba(255, 132, 76, 0.20)' },
                  '&:hover fieldset': { borderColor: '#f97316' },
                  '&.Mui-focused fieldset': { borderColor: '#f97316' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#f97316' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {timelineEntries.length > 0 && (
            <Box sx={{ overflowX: "auto", pb: 0.5 }}>
              <Box
                data-testid="execution-timeline-chart"
                sx={{ minWidth: 900, pt: 0.5 }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(220px, 28%) minmax(520px, 1fr) 72px",
                    columnGap: 1.5,
                    alignItems: "end",
                    mb: 1.25,
                  }}
                >
                  <Box />
                  <Box sx={{ position: "relative", height: 44 }}>
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 5,
                        height: 2,
                        borderRadius: 999,
                        backgroundColor: "#d6d9df",
                      }}
                    />
                    {timelineTicks.map((tick, index) => (
                      <React.Fragment key={tick.position}>
                        <Typography
                          variant="caption"
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: `${tick.position}%`,
                            transform:
                              index === 0
                                ? "none"
                                : index === timelineTicks.length - 1
                                  ? "translateX(-100%)"
                                  : "translateX(-50%)",
                            color: "#343a46",
                            fontSize: "0.82rem",
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          {tick.label}
                        </Typography>
                        <Box
                          sx={{
                            position: "absolute",
                            left: `${tick.position}%`,
                            bottom: 0,
                            width: 1.5,
                            height: 13,
                            transform: "translateX(-0.75px)",
                            backgroundColor: "#9ca2aa",
                          }}
                        />
                      </React.Fragment>
                    ))}
                  </Box>
                  <Box />
                </Box>

                <Box
                  sx={{
                    maxHeight: timelineEntries.length > 15 ? 620 : "none",
                    overflowY: timelineEntries.length > 15 ? "auto" : "visible",
                    pr: timelineEntries.length > 15 ? 0.75 : 0,
                  }}
                >
                  {timelineEntries.map(({ agent, startOffsetMs, durationMs }) => {
                    const color = getTimelineColor(agent.agentName);
                    const left = (startOffsetMs / timelineMaxMs) * 100;
                    const width = (durationMs / timelineMaxMs) * 100;
                    const duration = formatTimelineDuration(durationMs);

                    return (
                      <Box
                        key={agent.id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(220px, 28%) minmax(520px, 1fr) 72px",
                          columnGap: 1.5,
                          alignItems: "center",
                          minHeight: 40,
                          mb: 0.75,
                        }}
                      >
                        <Typography
                          title={agent.agentName}
                          noWrap
                          sx={{
                            pr: 1,
                            color: "#242b36",
                            fontSize: "1.05rem",
                            fontWeight: 750,
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {agent.agentName}
                        </Typography>
                        <Box
                          sx={{
                            position: "relative",
                            height: 32,
                            overflow: "hidden",
                            borderRadius: "10px",
                            backgroundColor: "#f1f2f5",
                            boxShadow: "inset 0 1px 1px rgba(15, 23, 42, 0.035)",
                          }}
                        >
                          <Tooltip
                            arrow
                            describeChild
                            placement="top"
                            enterDelay={500}
                            enterNextDelay={500}
                            title={
                              <TimelineResponseTooltip
                                response={agent.promptContent?.llmResponseFormated}
                              />
                            }
                            slotProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: "none",
                                  p: 1.5,
                                  borderRadius: "12px",
                                  backgroundColor: "#202531",
                                  boxShadow: "0 14px 32px rgba(15, 23, 42, 0.28)",
                                },
                              },
                            }}
                          >
                            <Box
                              component="button"
                              type="button"
                              aria-label={`Open ${agent.agentName} trace call`}
                              data-testid={`timeline-bar-${agent.id}`}
                              onClick={() => setSelectedAgent(agent)}
                              sx={{
                                position: "absolute",
                                top: 0,
                                bottom: 0,
                                left: `${left}%`,
                                width: `max(${width}%, 68px)`,
                                maxWidth: `calc(100% - ${left}%)`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1.5,
                                border: 0,
                                borderRadius: "10px",
                                background: `linear-gradient(135deg, ${color.fill} 0%, ${color.fill} 100%)`,
                                boxShadow: `0 8px 18px ${color.shadow}`,
                                color: "#fff",
                                cursor: "pointer",
                                transition: "filter 160ms ease, transform 160ms ease, box-shadow 160ms ease",
                                "&:hover": {
                                  filter: "brightness(1.04)",
                                  transform: "translateY(-1px)",
                                  boxShadow: `0 10px 22px ${color.shadow}`,
                                },
                                "&:focus-visible": {
                                  outline: "3px solid rgba(249, 115, 22, 0.28)",
                                  outlineOffset: 2,
                                },
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  color: "inherit",
                                  fontSize: "0.76rem",
                                  fontWeight: 800,
                                  whiteSpace: "nowrap",
                                  textShadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
                                }}
                              >
                                {duration}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                        <Typography
                          sx={{
                            color: "#374151",
                            fontSize: "0.88rem",
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {duration}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                <Box
                  aria-label="Timeline legend"
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 2.5,
                    mt: 2.5,
                    px: 0.5,
                  }}
                >
                  {timelineLegend.map((agentName) => {
                    const color = getTimelineColor(agentName);
                    return (
                      <Box key={agentName} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: color.fill,
                            boxShadow: `0 3px 8px ${color.shadow}`,
                          }}
                        />
                        <Typography sx={{ color: "#71808f", fontSize: "0.9rem", fontWeight: 600 }}>
                          {agentName}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}
          {agentFilterText.trim() && timelineEntries.length === 0 && (
            <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
              No agents match the filter.
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1, gap: 1 }}>
            {isAdmin && (
            <Tooltip title={agentData && agentData.length > 50 ? "Disabled: more than 50 agents executed" : ""}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleTraceLogs}
                  disabled={traceLogsLoading || (agentData && agentData.length > 50)}
                  startIcon={traceLogsLoading ? <CircularProgress size={16} color="inherit" /> : <ArticleOutlinedIcon />}
                  sx={{
                    background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                    "&:hover": { background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)" },
                    boxShadow: "0 14px 30px rgba(255, 139, 71, 0.28)",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    px: 2.5,
                  }}
                >
                  {traceLogsLoading ? "Loading..." : "Logs"}
                </Button>
              </span>
            </Tooltip>
            )}
            <Tooltip title={agentData && agentData.length > 15 ? "Disabled: more than 15 agents executed" : ""}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAiAnalyse}
                  disabled={aiAnalysisLoading || (agentData && agentData.length > 15)}
                  startIcon={aiAnalysisLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                  sx={{
                    background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                    "&:hover": { background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)" },
                    boxShadow: "0 14px 30px rgba(255, 139, 71, 0.28)",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    px: 2.5,
                  }}
                >
                  {aiAnalysisLoading ? "Analysing..." : "AI Analyser"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      )}

      <Dialog
        open={aiAnalysisDialogOpen}
        onClose={() => setAiAnalysisDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>
            AI Analysis
          </Typography>
          <IconButton onClick={() => setAiAnalysisDialogOpen(false)} sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <Box
            sx={{ color: "#374151", lineHeight: 1.7, "& p": { mt: 0, mb: 1.5 }, "& strong": { color: "inherit" }, "& ul, & ol": { pl: 2.5 }, "& code": { backgroundColor: "rgba(255, 132, 76, 0.08)", px: 0.5, borderRadius: "4px", fontSize: "0.875em" } }}
            dangerouslySetInnerHTML={{ __html: marked(aiAnalysisResult || "", { breaks: true }) }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setAiAnalysisDialogOpen(false)}
            sx={{
              color: "#f97316",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#fff7f1" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={traceLogsDialogOpen}
        onClose={() => setTraceLogsDialogOpen(false)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>
              Trace Logs
            </Typography>
            <Chip
              label={traceLogsData.length}
              size="small"
              sx={{
                height: 24,
                borderRadius: "8px",
                fontSize: "0.75rem",
                fontWeight: 700,
                backgroundColor: "rgba(249, 115, 22, 0.1)",
                color: "#f97316",
                border: "1px solid rgba(249, 115, 22, 0.3)",
              }}
            />
          </Box>
          <IconButton onClick={() => setTraceLogsDialogOpen(false)} sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(255, 132, 76, 0.10)", display: "flex", gap: 1.5, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Filter logs..."
              value={traceLogsFilter}
              onChange={(e) => setTraceLogsFilter(e.target.value)}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#fffaf7",
                  "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" },
                  "&:hover fieldset": { borderColor: "#f97316" },
                  "&.Mui-focused fieldset": { borderColor: "#f97316" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#f97316" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Select
              size="small"
              value={traceLogsTypeFilter}
              onChange={(e) => setTraceLogsTypeFilter(e.target.value)}
              sx={{
                minWidth: 140,
                borderRadius: "12px",
                backgroundColor: "#fffaf7",
                "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" },
                "&:hover fieldset": { borderColor: "#f97316" },
                "&.Mui-focused fieldset": { borderColor: "#f97316" },
              }}
            >
              <MenuItem value="All">All Types</MenuItem>
              {[...new Set(traceLogsData.map((l) => l.type))].map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ maxHeight: "60vh", overflow: "auto" }}>
            {(() => {
              const filtered = traceLogsData.filter((log) => {
                const matchesText = !traceLogsDebouncedFilter.trim() || log.message?.toLowerCase().includes(traceLogsDebouncedFilter.toLowerCase());
                const matchesType = traceLogsTypeFilter === "All" || log.type === traceLogsTypeFilter;
                return matchesText && matchesType;
              });
              return (
                <>
                  <Box sx={{ px: 2.5, py: 1, backgroundColor: "rgba(249, 115, 22, 0.04)", borderBottom: "1px solid rgba(255, 132, 76, 0.10)", display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Chip
                        label={`${traceLogsData.filter(l => l.type === "Information").length} Information`}
                        size="small"
                        sx={{ height: 22, borderRadius: "6px", fontSize: "0.7rem", fontWeight: 700, backgroundColor: "rgba(34, 197, 94, 0.08)", color: "#16a34a", border: "1px solid rgba(34, 197, 94, 0.3)" }}
                      />
                      <Chip
                        label={`${traceLogsData.filter(l => l.type === "Warning").length} Warning`}
                        size="small"
                        sx={{ height: 22, borderRadius: "6px", fontSize: "0.7rem", fontWeight: 700, backgroundColor: "rgba(234, 179, 8, 0.08)", color: "#ca8a04", border: "1px solid rgba(234, 179, 8, 0.3)" }}
                      />
                      <Chip
                        label={`${traceLogsData.filter(l => l.type === "Error").length} Error`}
                        size="small"
                        sx={{ height: 22, borderRadius: "6px", fontSize: "0.7rem", fontWeight: 700, backgroundColor: "rgba(239, 68, 68, 0.08)", color: "#dc2626", border: "1px solid rgba(239, 68, 68, 0.3)" }}
                      />
                    </Box>
                    {(traceLogsDebouncedFilter.trim() || traceLogsTypeFilter !== "All") && (
                      <Typography variant="caption" sx={{ color: "#f97316", fontWeight: 600 }}>
                        Showing {filtered.length} of {traceLogsData.length} logs
                      </Typography>
                    )}
                  </Box>
                  {filtered.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body2" sx={{ color: "#9ca3af" }}>No logs found.</Typography>
                    </Box>
                  ) : (
                    filtered.map((log, idx) => (
                      <Box
                        key={log.id || idx}
                        sx={{
                          px: 2.5,
                          py: 1.5,
                          borderBottom: "1px solid rgba(238, 240, 244, 1)",
                          "&:hover": { backgroundColor: "rgba(255, 247, 243, 0.72)" },
                        }}
                      >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Chip
                      label={log.type}
                      size="small"
                      sx={{
                        height: 22,
                        borderRadius: "6px",
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        backgroundColor: log.type === "Error" ? "rgba(239, 68, 68, 0.08)" : log.type === "Warning" ? "rgba(234, 179, 8, 0.08)" : "rgba(34, 197, 94, 0.08)",
                        color: log.type === "Error" ? "#dc2626" : log.type === "Warning" ? "#ca8a04" : "#16a34a",
                        border: `1px solid ${log.type === "Error" ? "rgba(239, 68, 68, 0.3)" : log.type === "Warning" ? "rgba(234, 179, 8, 0.3)" : "rgba(34, 197, 94, 0.3)"}`,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: "#9ca3af", fontWeight: 500 }}>
                      {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 })}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.5, wordBreak: "break-word" }}>
                    {log.message}
                  </Typography>
                </Box>
                    ))
                  )}
                </>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
            {traceLogsData.length} total logs
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => {
                const idx = traceLogsData.findIndex(l => l.message?.startsWith("Request Body:"));
                if (idx >= 0) handleOpenCurlReplay(idx);
              }}
              disabled={!traceLogsData.some(l => l.message?.startsWith("Request Body:"))}
              startIcon={<ReplayIcon />}
              sx={{
                color: "#f97316",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#fff7f1" },
                "&.Mui-disabled": { color: "#d1d5db" },
              }}
            >
              Replay Api Request
            </Button>
            <Button
              onClick={() => {
                const blob = new Blob([JSON.stringify(traceLogsData, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `trace-logs-${traceId}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              startIcon={<DownloadIcon />}
              sx={{
                color: "#f97316",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#fff7f1" },
              }}
            >
              Download JSON
            </Button>
            <Button
              onClick={() => setTraceLogsDialogOpen(false)}
              sx={{
                color: "#f97316",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#fff7f1" },
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={curlDialogOpen}
        onClose={() => setCurlDialogOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
            minHeight: "70vh",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ReplayIcon sx={{ color: "#f97316" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>
              Replay Request
            </Typography>
          </Box>
          <IconButton onClick={() => setCurlDialogOpen(false)} sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, display: "flex", flexDirection: "column" }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#374151", mb: 1 }}>
              Request URL
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Select
                size="small"
                value={curlReplayMethod}
                onChange={(e) => setCurlReplayMethod(e.target.value)}
                sx={{
                  minWidth: 100,
                  borderRadius: "12px",
                  backgroundColor: "#fffaf7",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" },
                  "&:hover fieldset": { borderColor: "#f97316" },
                  "&.Mui-focused fieldset": { borderColor: "#f97316" },
                }}
              >
                {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
              <UrlEditor
                value={curlReplayUrl}
                onChange={(newUrl) => {
                  setCurlReplayUrl(newUrl);
                  setCurlCommand((prev) => prev.replace(/'https?:\/\/[^']+'/,  `'${newUrl}'`));
                }}
              />
            </Box>
          </Box>

          <Tabs
            value={curlReplayTab}
            onChange={(_, v) => setCurlReplayTab(v)}
            sx={{
              mb: 2,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.85rem",
                color: "#6b7280",
                "&.Mui-selected": { color: "#f97316" },
              },
              "& .MuiTabs-indicator": { backgroundColor: "#f97316" },
            }}
          >
            <Tab label="Request" />
            <Tab label="cURL Command" />
          </Tabs>

          {curlReplayTab === 1 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ position: "relative" }}>
                <TextField
                  fullWidth
                  multiline
                  value={curlCommand}
                  onChange={(e) => {
                    const newCurl = e.target.value;
                    setCurlCommand(newCurl);
                    const urlMatch = newCurl.match(/'(https?:\/\/[^']+)'/);
                    if (urlMatch) setCurlReplayUrl(urlMatch[1]);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#1e1e2e",
                      fontFamily: "monospace",
                      fontSize: "0.78rem",
                      lineHeight: 1.6,
                      color: "#cdd6f4",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(255, 132, 76, 0.30)" },
                      "&.Mui-focused fieldset": { borderColor: "#f97316" },
                    },
                    "& .MuiInputBase-input": {
                      maxHeight: "400px",
                      overflow: "auto !important",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                    },
                  }}
                />
                <IconButton
                  size="large"
                  onClick={() => navigator.clipboard.writeText(curlCommand)}
                  title="Copy to clipboard"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "#cdd6f4",
                    backgroundColor: "rgba(255,255,255,0.12)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 50 }} />
                </IconButton>
              </Box>
            </Box>
          )}

          {curlReplayTab === 0 && (
            <Box sx={{ mb: 2 }}>
              <Accordion
                defaultExpanded={false}
                sx={{
                  mb: 2,
                  borderRadius: "12px !important",
                  border: "1px solid rgba(255, 132, 76, 0.12)",
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { margin: "0 0 16px 0" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#f97316" }} />}
                  sx={{
                    backgroundColor: "rgba(249, 115, 22, 0.04)",
                    borderRadius: "12px",
                    minHeight: 44,
                    "&.Mui-expanded": { minHeight: 44 },
                    "& .MuiAccordionSummary-content": { margin: "8px 0" },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#374151" }}>
                    Headers ({curlReplayHeaders.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1.5 }}>
                  <TableContainer sx={{ borderRadius: "8px" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: "#374151", fontSize: "0.78rem" }}>Key</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "#374151", fontSize: "0.78rem" }}>Value</TableCell>
                          <TableCell sx={{ width: 40 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {curlReplayHeaders.map((header, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ p: 0.5 }}>
                              <TextField
                                fullWidth
                                size="small"
                                value={header.key}
                                onChange={(e) => {
                                  const updated = [...curlReplayHeaders];
                                  updated[idx] = { ...updated[idx], key: e.target.value };
                                  setCurlReplayHeaders(updated);
                                }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.78rem", fontFamily: "monospace" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>
                              <TextField
                                fullWidth
                                size="small"
                                value={header.value}
                                onChange={(e) => {
                                  const updated = [...curlReplayHeaders];
                                  updated[idx] = { ...updated[idx], value: e.target.value };
                                  setCurlReplayHeaders(updated);
                                }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.78rem", fontFamily: "monospace" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => setCurlReplayHeaders(curlReplayHeaders.filter((_, i) => i !== idx))}
                                sx={{ color: "#dc2626" }}
                              >
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    size="small"
                    onClick={() => setCurlReplayHeaders([...curlReplayHeaders, { key: "", value: "" }])}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#f97316", mt: 1, "&:hover": { backgroundColor: "#fff7f1" } }}
                  >
                    + Add Header
                  </Button>
                </AccordionDetails>
              </Accordion>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#374151", mb: 1 }}>
                Body
              </Typography>
              <BodyEditor value={curlReplayBody} onChange={setCurlReplayBody} />
            </Box>
          )}

          {curlReplayError && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: "10px", backgroundColor: "rgba(239, 68, 68, 0.06)", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
              <Typography variant="body2" sx={{ color: "#dc2626", fontWeight: 600 }}>
                {curlReplayError}
              </Typography>
            </Box>
          )}
          {curlReplayResult && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#374151", mb: 1 }}>
                Response
              </Typography>
              <pre
                style={{
                  backgroundColor: "#f8fafc",
                  color: "#1e293b",
                  padding: "16px",
                  borderRadius: "12px",
                  fontSize: "0.78rem",
                  lineHeight: 1.5,
                  overflow: "auto",
                  maxHeight: "300px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  margin: 0,
                  border: curlReplaySuccess ? "2px solid #22c55e" : "2px solid #ef4444",
                }}
              >
                {curlReplayResult}
              </pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleExecuteReplay}
            disabled={curlReplayLoading}
            variant="contained"
            startIcon={curlReplayLoading ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
            sx={{
              background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
              "&:hover": { background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)" },
              boxShadow: "0 12px 24px rgba(255, 139, 71, 0.22)",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              px: 2.5,
            }}
          >
            {curlReplayLoading ? "Sending..." : "Send Request"}
          </Button>
          <Button
            onClick={() => setCurlDialogOpen(false)}
            sx={{
              color: "#6b7280",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#f9fafb" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {agentData && (
        <Paper elevation={0} sx={sectionPaperSx}>
          <Typography variant="h6" gutterBottom>
            Execution Summary
          </Typography>
          <Grid container spacing={3}>
            {/* Left Column - Agent Summary */}
            <Grid item xs={12} lg={chatMessages.length > 0 ? 8.4 : 12}>
              {userPrompt && (
                <Typography variant="subtitle1" sx={{ mb: 1, color: "#202531", fontWeight: 600 }}>
                  <strong>User prompt:</strong>{" "}
                  <Box component="span" sx={{ color: "#c25b12" }}>
                    {userPrompt}
                  </Box>
                </Typography>
              )}
              {tenantInfo?.tenantId && (
                <Typography variant="subtitle1" sx={{ mb: 1, color: "#202531", fontWeight: 600 }}>
                  <strong>Tenant ID:</strong> {tenantInfo.tenantId}
                </Typography>
              )}
              {tenantInfo?.tenantName && (
                <Typography variant="subtitle1" sx={{ mb: 1, color: "#202531", fontWeight: 600 }}>
                  <strong>Tenant Name:</strong> {tenantInfo.tenantName}
                </Typography>
              )}
              <Grid container spacing={2} sx={{ mt: 1 }} direction="column">
                {filteredSummaryEntries.map((entry, idx) => (
                  <Grid item key={idx} xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "20px",
                        border: "1px solid rgba(255, 132, 76, 0.10)",
                        background: idx % 2 === 0
                          ? "linear-gradient(180deg, rgba(255, 247, 241, 0.96), rgba(255,255,255,0.92))"
                          : "linear-gradient(180deg, rgba(255, 250, 245, 0.98), rgba(255,255,255,0.92))",
                        boxShadow: "0 18px 36px rgba(236, 123, 55, 0.07)",
                        cursor: 'pointer',
                        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                        '&:hover': {
                          transform: "translateY(-1px)",
                          borderColor: "rgba(249, 115, 22, 0.22)",
                          boxShadow: "0 22px 42px rgba(236, 123, 55, 0.10)",
                        },
                        display: 'block',
                      }}
                      onClick={() => handleOpenDrawer(entry)}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          color: "#202531",
                          mb: 0.75,
                        }}
                      >
                        Agent: <Box component="span" sx={{ color: "#c25b12" }}>{entry.name}</Box>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6b7280",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.5,
                        }}
                      >
                        Response: {entry.response}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Right Column - Chat History (Conditional) */}
            {chatMessages.length > 0 && (
              <Grid item xs={12} lg={3.6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: '800px',
                    borderRadius: "22px",
                    border: "1px solid rgba(255, 132, 76, 0.10)",
                    background: "linear-gradient(180deg, rgba(255, 250, 246, 0.98), rgba(255,255,255,0.94))",
                    boxShadow: "0 20px 40px rgba(236, 123, 55, 0.08)",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: "#202531" }}>
                    Chat History
                  </Typography>
                  <Box sx={{
                    height: '750px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25
                  }}>
                    {chatMessages.map((message, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                          mb: 0.25
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            maxWidth: '80%',
                            border: "1px solid rgba(255, 132, 76, 0.10)",
                            boxShadow: "0 12px 24px rgba(236, 123, 55, 0.06)",
                            background: message.type === 'user'
                              ? "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)"
                              : "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255, 247, 241, 0.94))",
                            color: message.type === 'user' ? '#fff' : '#202531',
                            borderRadius: message.type === 'user'
                              ? '18px 18px 6px 18px'
                              : '18px 18px 18px 6px'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.5 }}>
                            {message.text}
                          </Typography>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Cost Breakdown Section */}
      {agentData && (
        <Paper elevation={0} sx={sectionPaperSx}>
          <Typography variant="h6" gutterBottom>
            Cost Breakdown Analysis
          </Typography>

          {/* Model Pricing Reference */}
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="bold">
                Model Pricing Reference (per 1M tokens)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Model</strong></TableCell>
                      <TableCell align="center"><strong>Input (USD)</strong></TableCell>
                      <TableCell align="center"><strong>Output (USD)</strong></TableCell>
                      <TableCell align="center"><strong>Reasoning (USD)</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(OPENAI_PRICING).map(([model, pricing]) => (
                      <TableRow key={model}>
                        <TableCell>{model}</TableCell>
                        <TableCell align="center">${pricing.input.toFixed(2)}</TableCell>
                        <TableCell align="center">${pricing.output.toFixed(2)}</TableCell>
                        <TableCell align="center">${getReasoningTokenPrice(model, pricing).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Web search tool calls: ${WEB_SEARCH_PRICE_PER_1000_CALLS_USD.toFixed(2)} per 1,000 calls.
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Model Breakdown */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="bold">
                Cost by Model
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#64b5f6" }}>
                      <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 180, width: "18%" }}>Model</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold", minWidth: 64, width: "7%" }}>Calls</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Input Tokens</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Output Tokens</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Reasoning Tokens</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Search Calls</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Input Cost (USD)</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Output Cost (USD)</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Reasoning Cost (USD)</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Web Search Cost (USD)</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Total (USD)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(costBreakdown.byModel).map(([model, data], index) => {
                      const colors = [
                        "#e8f5e8", // Light green
                        "#fff3e0", // Light orange
                        "#e3f2fd", // Light blue
                        "#f3e5f5", // Light purple
                        "#fce4ec", // Light pink
                        "#e0f2f1", // Light teal
                        "#fff8e1", // Light yellow
                        "#ffebee" // Light red
                      ];
                      const backgroundColor = colors[index % colors.length];

                      return (
                        <TableRow key={model} sx={{ backgroundColor, '&:hover': { backgroundColor: backgroundColor, filter: 'brightness(0.95)' } }}>
                          <TableCell sx={{ fontWeight: "600", minWidth: 180, width: "18%" }}>{model}</TableCell>
                          <TableCell align="center" sx={{ minWidth: 64, width: "7%" }}>{data.count}</TableCell>
                          <TableCell align="center">{formatNumber(data.totalTokens.input)}</TableCell>
                          <TableCell align="center">{formatNumber(data.totalTokens.output)}</TableCell>
                          <TableCell align="center">{formatNumber(data.totalTokens.reasoning)}</TableCell>
                          <TableCell align="center">{formatNumber(data.totalSearchCalls)}</TableCell>
                          <TableCell align="center">{formatCurrency(data.totalCost.input, 'USD')}</TableCell>
                          <TableCell align="center">{formatCurrency(data.totalCost.output, 'USD')}</TableCell>
                          <TableCell align="center">{formatCurrency(data.totalCost.reasoning, 'USD')}</TableCell>
                          <TableCell align="center">{formatCurrency(data.totalCost.webSearch, 'USD')}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "600" }}>{formatCurrency(data.totalCost.usd, 'USD')}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={{ backgroundColor: "#64b5f6", color: "white" }}>
                      <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 180, width: "18%" }}>TOTAL</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold", minWidth: 64, width: "7%" }}>
                        {Object.values(costBreakdown.byModel).reduce((sum, data) => sum + data.count, 0)}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                        {formatNumber(Object.values(costBreakdown.byModel).reduce((sum, data) => sum + data.totalTokens.input, 0))}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                        {formatNumber(Object.values(costBreakdown.byModel).reduce((sum, data) => sum + data.totalTokens.output, 0))}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                        {formatNumber(Object.values(costBreakdown.byModel).reduce((sum, data) => sum + data.totalTokens.reasoning, 0))}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                        {formatNumber(Object.values(costBreakdown.byModel).reduce((sum, data) => sum + data.totalSearchCalls, 0))}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                        {formatCurrency(costBreakdown.byTokenType.input, 'USD')}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                        {formatCurrency(costBreakdown.byTokenType.output, 'USD')}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                        {formatCurrency(costBreakdown.byTokenType.reasoning, 'USD')}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                        {formatCurrency(costBreakdown.byTokenType.webSearch, 'USD')}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                        {formatCurrency(costBreakdown.totalCost.usd, 'USD')}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}

      {error && (
        <Typography color="error" mt={2} sx={{ fontWeight: 600 }}>
          {error}
        </Typography>
      )}

      {agentData && (
        <Paper elevation={0} sx={sectionPaperSx}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Agent Trace Data
            </Typography>
            <IconButton
              size="small"
              title="Download JSON"
              onClick={() => {
                const blob = new Blob([JSON.stringify(agentData, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `agent-trace-${traceId || "data"}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setShowAgentTraceData((p) => !p)}
            >
              {showAgentTraceData ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          {showAgentTraceData && (
            <Box sx={{ overflowX: "auto" }}>
              <TextField
                fullWidth
                size="small"
                label="Filter trace items"
                value={agentTraceDataFilter}
                onChange={(e) => setAgentTraceDataFilter(e.target.value)}
                placeholder="Type a word to filter the list"
                sx={{ mb: 1.5 }}
              />
              <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
                Showing {filteredAgentTraceData.length} of {agentData.length} items
              </Typography>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {highlightedAgentTraceJson}
              </pre>
            </Box>
          )}
        </Paper>
      )}
      </Box>

      <Dialog
        open={Boolean(selectedAgent)}
        onClose={handleCloseAgentDialog}
        fullWidth
        fullScreen={dialogMaximized}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: dialogMaximized ? "100%" : "90vw",
            maxWidth: dialogMaximized ? "none" : "90vw",
            m: dialogMaximized ? 0 : "5vw",
            borderRadius: dialogMaximized ? 0 : "28px",
            border: dialogMaximized ? "none" : "1px solid rgba(255, 132, 76, 0.12)",
            background:
              "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: dialogMaximized ? "none" : "0 30px 70px rgba(236, 123, 55, 0.16)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                boxShadow: "0 14px 28px rgba(255, 139, 71, 0.24)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
                LLM Call Details
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
                {selectedAgent?.agentName} • {selectedAgent && `${selectedAgent.durationMs}ms • ${selectedAgent.promptContent?.llmModel || "No model"}`}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              aria-label="maximize"
              onClick={() => setDialogMaximized((v) => !v)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "14px",
                color: "#6b7280",
                "&:hover": {
                  backgroundColor: "#fff7f1",
                  color: "#f97316",
                },
              }}
            >
              {dialogMaximized ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
            </IconButton>
            <IconButton
              aria-label="close"
              onClick={handleCloseAgentDialog}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "14px",
                color: "#6b7280",
                "&:hover": {
                  backgroundColor: "#fff7f1",
                  color: "#f97316",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2.5,
            backgroundColor: "transparent",
            borderTop: 0,
            borderBottom: 0,
          }}
        >
          {selectedAgent && agentEdits && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.75,
                  borderRadius: "18px",
                  backgroundColor: "rgba(255,255,255,0.84)",
                  borderColor: "rgba(255, 132, 76, 0.10)",
                  boxShadow: "0 14px 30px rgba(236, 123, 55, 0.05)",
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.75 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#202531" }}>
                    Prompt Arguments
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
                    {argsExpanded && (
                      <Tooltip
                        title="Pretty-print JSON and show escaped text as readable content"
                        describeChild
                      >
                        <FormControlLabel
                          control={(
                            <Checkbox
                              size="small"
                              checked={argsFormatAsJson}
                              onChange={(event) => setArgsFormatAsJson(event.target.checked)}
                              sx={{
                                p: 0.5,
                                color: "#9ca3af",
                                "&.Mui-checked": { color: "#f97316" },
                                "& .MuiSvgIcon-root": { fontSize: 19 },
                              }}
                            />
                          )}
                          label={(
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.55 }}>
                              <DataObjectOutlinedIcon sx={{ fontSize: 16 }} />
                              <Box component="span">Format as JSON</Box>
                            </Box>
                          )}
                          sx={{
                            m: 0,
                            minHeight: 34,
                            pl: 0.35,
                            pr: 1.1,
                            borderRadius: "12px",
                            border: "1px solid",
                            borderColor: argsFormatAsJson
                              ? "rgba(249, 115, 22, 0.38)"
                              : "rgba(15, 23, 42, 0.10)",
                            color: argsFormatAsJson ? "#c2410c" : "#4b5563",
                            backgroundColor: argsFormatAsJson
                              ? "rgba(255, 237, 213, 0.76)"
                              : "rgba(255,255,255,0.72)",
                            boxShadow: argsFormatAsJson
                              ? "0 5px 14px rgba(249, 115, 22, 0.12)"
                              : "none",
                            cursor: "pointer",
                            transition: "all 160ms ease",
                            "&:hover": {
                              borderColor: "rgba(249, 115, 22, 0.34)",
                              backgroundColor: "rgba(255, 247, 237, 0.95)",
                            },
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.78rem",
                              fontWeight: 750,
                              lineHeight: 1,
                            },
                          }}
                        />
                      </Tooltip>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setArgsExpanded(!argsExpanded)}
                      startIcon={argsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{
                        minHeight: 34,
                        px: 1.4,
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: "#f97316",
                        borderColor: "rgba(249, 115, 22, 0.26)",
                        backgroundColor: "rgba(255, 247, 241, 0.86)",
                      }}
                    >
                      {argsExpanded ? "Collapse" : "Expand"}
                    </Button>
                  </Box>
                </Box>
                {promptArgumentsJson !== null ? (
                  <JsonSyntaxBlock
                    value={promptArgumentsDisplay}
                    testId="prompt-arguments-value"
                    compact={!argsExpanded}
                  />
                ) : (
                  <Box
                    sx={{
                      p: 1,
                      height: argsExpanded ? 240 : "auto",
                      minHeight: argsExpanded ? 160 : 0,
                      maxHeight: argsExpanded ? "72vh" : 160,
                      resize: argsExpanded ? "vertical" : "none",
                      backgroundColor: "#fff",
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      borderRadius: "14px",
                      overflow: 'auto'
                    }}
                  >
                    <pre data-testid="prompt-arguments-value" style={{
                      margin: 0,
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      whiteSpace: argsExpanded ? "pre-wrap" : "pre",
                      wordBreak: argsExpanded ? "break-word" : "normal",
                      fontSize: "0.72rem",
                      lineHeight: 1.4,
                      maxHeight: argsExpanded ? 'none' : '160px',
                      overflowY: argsExpanded ? 'visible' : 'auto'
                    }}>
                      {promptArgumentsDisplay}
                    </pre>
                  </Box>
                )}
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.75,
                  borderRadius: "18px",
                  backgroundColor: "rgba(255,255,255,0.84)",
                  borderColor: "rgba(255, 132, 76, 0.10)",
                  boxShadow: "0 14px 30px rgba(236, 123, 55, 0.05)",
                }}
              >
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#202531", mb: 0.75 }}>
                  Request
                </Typography>
                {llmRequestFields ? (
                  <SplitLlmRequestFields
                    request={llmRequestFields}
                    onChange={handleUpdateLlmRequestFormated}
                  />
                ) : (
                  <CollapsibleField
                    label="LLM Request Formated"
                    jsonMode
                    value={agentEdits.llmRequestFormated}
                    onChange={handleUpdateLlmRequestFormated}
                  />
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleTestPrompt}
                    disabled={testPromptLoading}
                    sx={{
                      minHeight: 40,
                      px: 2,
                      borderRadius: "12px",
                      textTransform: 'none',
                      fontWeight: 700,
                      color: "#fff",
                      background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                      boxShadow: "0 12px 24px rgba(255, 139, 71, 0.22)",
                    }}
                  >
                    {testPromptLoading ? "Testing..." : "Test Prompt"}
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="test-prompt-model-label">Model</InputLabel>
                    <Select
                      labelId="test-prompt-model-label"
                      label="Model"
                      value={testPromptModelId}
                      onChange={(e) => {
                        const model = e.target.value;
                        setTestPromptModelId(model);
                        setTestPromptReasoningEffortLevel((effort) => normalizeReasoningEffort(model, effort));
                      }}
                      disabled={testPromptLoading}
                      sx={{ fontSize: '0.82rem' }}
                    >
                      {testPromptModelOptions.map((modelId) => (
                        <MenuItem key={modelId} value={modelId} sx={{ fontSize: '0.82rem' }}>
                          {modelId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {shouldShowReasoningEffortLevel && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="test-prompt-reasoning-effort-level-label">
                        Reasoning Effort
                      </InputLabel>
                      <Select
                        labelId="test-prompt-reasoning-effort-level-label"
                        label="Reasoning Effort"
                        value={testPromptReasoningEffortLevel}
                        onChange={(e) => setTestPromptReasoningEffortLevel(e.target.value)}
                        disabled={testPromptLoading}
                        sx={{ fontSize: '0.82rem' }}
                      >
                        {reasoningEffortOptions.map((level) => (
                          <MenuItem key={level} value={level} sx={{ fontSize: '0.82rem' }}>
                            {level}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {shouldShowWebSearchControls && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="test-prompt-web-search-label">web_search</InputLabel>
                      <Select
                        labelId="test-prompt-web-search-label"
                        label="web_search"
                        value={testPromptWebSearch}
                        onChange={(e) => setTestPromptWebSearch(e.target.value)}
                        disabled={testPromptLoading}
                        sx={{ fontSize: "0.82rem" }}
                      >
                        {WEB_SEARCH_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.82rem" }}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {shouldShowAllowedDomainsInput && (
                    <TextField
                      size="small"
                      label="allowed_domains"
                      value={testPromptAllowedDomains}
                      onChange={(e) => setTestPromptAllowedDomains(e.target.value)}
                      disabled={testPromptLoading}
                      placeholder="example.com, api.example.com"
                      sx={{ minWidth: 280 }}
                    />
                  )}
                </Box>
                {testPromptResult && (
                  <Box
                    data-testid="response-comparison"
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.5,
                      mt: 1.5,
                      alignItems: "stretch",
                    }}
                  >
                    {testPromptCostComparison && (
                      <Box
                        data-testid="test-cost-comparison"
                        sx={{
                          gridColumn: "1 / -1",
                          px: 1.5,
                          py: 1.1,
                          border: "1px solid",
                          borderColor: testPromptCostComparison.color,
                          borderRadius: "14px",
                          backgroundColor: testPromptCostComparison.backgroundColor,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{ color: testPromptCostComparison.color }}
                        >
                          Cost comparison: {testPromptCostComparison.text}
                        </Typography>
                      </Box>
                    )}
                    <Paper
                      data-testid="original-response-column"
                      variant="outlined"
                      sx={{
                        minWidth: 0,
                        p: 1.5,
                        borderRadius: "16px",
                        borderColor: "rgba(15, 23, 42, 0.10)",
                        backgroundColor: "rgba(248, 250, 252, 0.86)",
                        height: "100%",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#202531" }}>
                          Original Response
                        </Typography>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              size="small"
                              checked={formatResponseText}
                              onChange={(event) => setFormatResponseText(event.target.checked)}
                            />
                          )}
                          label="Format text"
                          sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.75rem" } }}
                        />
                      </Box>
                      <CollapsibleField
                        label="LLM Response Formated ( Original )"
                        jsonMode={formatResponseText}
                        alwaysExpanded
                        highlightJson={formatResponseText}
                        formatText={formatResponseText}
                        value={agentEdits.llmResponseFormated}
                        onChange={(value) =>
                          setAgentEdits((current) => ({
                            ...current,
                            llmResponseFormated: value,
                          }))
                        }
                      />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: -1.2, mb: 1.25, color: "#94a3b8", fontSize: "0.68rem" }}
                      >
                        Trace ID: <Box component="span" sx={{ fontFamily: "monospace" }}>{traceId || "N/A"}</Box>
                      </Typography>
                    </Paper>

                    <Paper
                      data-testid="test-response-column"
                      variant="outlined"
                      sx={{
                        minWidth: 0,
                        p: 1.5,
                        borderRadius: "16px",
                        borderColor: "rgba(255, 132, 76, 0.20)",
                        backgroundColor: "rgba(255, 247, 241, 0.82)",
                        height: "100%",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#c2410c" }}>
                          Test Response
                        </Typography>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              size="small"
                              checked={formatTestResponseText}
                              onChange={(event) => setFormatTestResponseText(event.target.checked)}
                            />
                          )}
                          label="Format text"
                          sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.75rem" } }}
                        />
                      </Box>
                      <CollapsibleField
                        key={testPromptResponseAgent?.id || "test-prompt-result"}
                        label="LLM Response Formated ( Test )"
                        jsonMode={formatTestResponseText}
                        alwaysExpanded
                        highlightJson={formatTestResponseText}
                        formatText={formatTestResponseText}
                        value={testPromptResponseValue}
                        onChange={() => { }}
                      />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: -1.2, mb: 1.25, color: "#a8a29e", fontSize: "0.68rem" }}
                      >
                        Trace ID: <Box component="span" sx={{ fontFamily: "monospace" }}>{testPromptTraceId || "N/A"}</Box>
                      </Typography>
                      {testPromptTraceError && (
                        <Typography color="error" variant="caption" display="block" sx={{ mb: 1 }}>
                          {testPromptTraceError}
                        </Typography>
                      )}
                    </Paper>
                    <ResponseMetricsComparison
                      originalAgent={selectedAgent}
                      testAgent={testPromptResponseAgent}
                      formatNumberValue={formatNumber}
                      formatCurrencyValue={formatCurrency}
                    />
                    <Box
                      sx={{
                        gridColumn: "1 / -1",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                        gap: 1.5,
                        alignItems: "start",
                      }}
                    >
                      <Box data-testid="original-response-trace-details">
                        <LlmTraceDetails promptContent={selectedAgent.promptContent} />
                      </Box>
                      {testPromptResponseAgent ? (
                        <Box data-testid="test-response-trace-details">
                          <LlmTraceDetails promptContent={testPromptResponseAgent.promptContent} />
                        </Box>
                      ) : (
                        <Box />
                      )}
                    </Box>
                  </Box>
                )}
                {testPromptTraceAgents.length > 0 && !testPromptResponseAgent && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mt: 1,
                      borderRadius: "16px",
                      backgroundColor: "rgba(255, 247, 241, 0.72)",
                      borderColor: "rgba(255, 132, 76, 0.10)",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#202531", mb: 1 }}>
                      Agent Information (From Test Result)
                    </Typography>
                    {testPromptTraceAgents.map((agent, idx) => (
                      <Box
                        key={agent.id || `${agent.agentName}-${idx}`}
                        sx={{
                          p: 1,
                          mb: idx < testPromptTraceAgents.length - 1 ? 1 : 0,
                          borderRadius: 0.5,
                          border: '1px solid #e8e0ec'
                        }}
                      >
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                          {agent.agentName || "Unknown Agent"}
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary" display="block">Duration</Typography>
                            <Typography variant="body2" fontWeight={600}>{agent.durationMs || 0}ms</Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary" display="block">Model</Typography>
                            <Typography variant="body2" fontWeight={600}>{agent.promptContent?.llmModel || "N/A"}</Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary" display="block">Type</Typography>
                            <Typography variant="body2" fontWeight={600}>{agent.promptContent?.type || "N/A"}</Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary" display="block">Timestamp</Typography>
                            <Typography variant="body2" fontWeight={600}>{agent["@timestamp"] ? new Date(agent["@timestamp"]).toLocaleString() : "N/A"}</Typography>
                          </Grid>
                        </Grid>
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1, pt: 1 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={4} md={2}>
                              <Typography variant="caption" color="text.secondary" display="block">Prompt Tokens</Typography>
                              <Typography variant="body2" fontWeight={600}>{formatNumber(agent.promptContent?.promptTokensFromResponse || 0)}</Typography>
                            </Grid>
                            <Grid item xs={4} md={2}>
                              <Typography variant="caption" color="text.secondary" display="block">Completion Tokens</Typography>
                              <Typography variant="body2" fontWeight={600}>{formatNumber(agent.promptContent?.completionTokensFromResponse || 0)}</Typography>
                            </Grid>
                            <Grid item xs={4} md={2}>
                              <Typography variant="caption" color="text.secondary" display="block">Reasoning Tokens</Typography>
                              <Typography variant="body2" fontWeight={600}>{formatNumber(agent.promptContent?.reasoningTokensFromResponse || 0)}</Typography>
                            </Grid>
                            <Grid item xs={4} md={2}>
                              <Typography variant="caption" color="text.secondary" display="block">Search Calls</Typography>
                              <Typography variant="body2" fontWeight={600}>{formatNumber(getSearchCallCount(agent.promptContent))}</Typography>
                            </Grid>
                            {(() => {
                              const cost = calculateCost(
                                agent.promptContent?.llmModel,
                                agent.promptContent?.promptTokensFromResponse,
                                agent.promptContent?.completionTokensFromResponse,
                                agent.promptContent?.reasoningTokensFromResponse,
                                getSearchCallCount(agent.promptContent)
                              );
                              return (
                                <>
                                  <Grid item xs={4} md={2}>
                                    <Typography variant="caption" color="text.secondary" display="block">Input Cost</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatCurrency(cost.inputCost, 'USD')}</Typography>
                                  </Grid>
                                  <Grid item xs={4} md={2}>
                                    <Typography variant="caption" color="text.secondary" display="block">Output Cost</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatCurrency(cost.outputCost, 'USD')}</Typography>
                                  </Grid>
                                  <Grid item xs={4} md={2}>
                                    <Typography variant="caption" color="text.secondary" display="block">Web Search Cost</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatCurrency(cost.webSearchCost, 'USD')}</Typography>
                                  </Grid>
                                  <Grid item xs={4} md={2}>
                                    <Typography variant="caption" color="text.secondary" display="block">Total Cost</Typography>
                                    <Typography variant="body2" fontWeight={700} color="primary.main">{formatCurrency(cost.usd, 'USD')}</Typography>
                                  </Grid>
                                </>
                              );
                            })()}
                          </Grid>
                        </Box>
                      </Box>
                    ))}
                    {(() => {
                      const originalCost = calculateCost(
                        selectedAgent.promptContent?.llmModel,
                        selectedAgent.promptContent?.promptTokensFromResponse,
                        selectedAgent.promptContent?.completionTokensFromResponse,
                        selectedAgent.promptContent?.reasoningTokensFromResponse,
                        getSearchCallCount(selectedAgent.promptContent)
                      );
                      const testTotalCost = testPromptTraceAgents.reduce((sum, agent) => {
                        const c = calculateCost(
                          agent.promptContent?.llmModel,
                          agent.promptContent?.promptTokensFromResponse,
                          agent.promptContent?.completionTokensFromResponse,
                          agent.promptContent?.reasoningTokensFromResponse,
                          getSearchCallCount(agent.promptContent)
                        );
                        return sum + c.usd;
                      }, 0);

                      if (originalCost.usd === 0 && testTotalCost === 0) return null;

                      let comparisonText = "";
                      let comparisonColor = "text.secondary";

                      if (originalCost.usd === 0) {
                        comparisonText = `Test cost: ${formatCurrency(testTotalCost, "USD")} (no original cost to compare)`;
                      } else if (testTotalCost === 0) {
                        comparisonText = "Test cost: $0.00 (no cost)";
                      } else {
                        const ratio = testTotalCost / originalCost.usd;
                        if (ratio > 1) {
                          comparisonText = `${ratio.toFixed(2)}x more expensive than original prompt (${formatCurrency(originalCost.usd, "USD")} → ${formatCurrency(testTotalCost, "USD")})`;
                          comparisonColor = "#d32f2f";
                        } else if (ratio < 1) {
                          const savings = (1 / ratio).toFixed(2);
                          comparisonText = `${savings}x cheaper than original prompt (${formatCurrency(originalCost.usd, "USD")} → ${formatCurrency(testTotalCost, "USD")})`;
                          comparisonColor = "#2e7d32";
                        } else {
                          comparisonText = `Same cost (${formatCurrency(originalCost.usd, "USD")})`;
                        }
                      }

                      return (
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" fontWeight={700} sx={{ color: comparisonColor }}>
                            {comparisonText}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Paper>
                )}
              </Paper>

              {!testPromptResult && (
                <Paper
                  data-testid="llm-response-panel"
                  variant="outlined"
                  sx={{
                    p: 1.75,
                    borderRadius: "18px",
                    backgroundColor: "rgba(255,255,255,0.84)",
                    borderColor: "rgba(255, 132, 76, 0.10)",
                    boxShadow: "0 14px 30px rgba(236, 123, 55, 0.05)",
                  }}
                >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 0.75,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#202531" }}>
                    Response
                  </Typography>
                  <FormControlLabel
                      control={(
                        <Checkbox
                          size="small"
                          checked={formatResponseText}
                          onChange={(event) => setFormatResponseText(event.target.checked)}
                          sx={{
                            p: 0.5,
                            color: "#9ca3af",
                            "&.Mui-checked": { color: "#f97316" },
                          }}
                        />
                      )}
                      label="Format text"
                      sx={{
                        m: 0,
                        pl: 0.4,
                        pr: 1.1,
                        minHeight: 34,
                        border: "1px solid",
                        borderColor: formatResponseText
                          ? "rgba(249, 115, 22, 0.38)"
                          : "rgba(15, 23, 42, 0.10)",
                        borderRadius: "12px",
                        backgroundColor: formatResponseText
                          ? "rgba(255, 237, 213, 0.76)"
                          : "rgba(255,255,255,0.72)",
                        "& .MuiFormControlLabel-label": {
                          color: formatResponseText ? "#c2410c" : "#4b5563",
                          fontSize: "0.78rem",
                          fontWeight: 750,
                        },
                      }}
                    />
                </Box>
                <CollapsibleField
                  label="LLM Response Formated"
                  jsonMode={formatResponseText}
                  alwaysExpanded
                  highlightJson={formatResponseText}
                  formatText={formatResponseText}
                  value={agentEdits.llmResponseFormated}
                  onChange={(v) =>
                    setAgentEdits((p) => ({ ...p, llmResponseFormated: v }))
                  }
                />
                <LlmTraceDetails promptContent={selectedAgent.promptContent} />
                </Paper>
              )}

              {!testPromptResult && (
                <Paper
                  data-testid="original-agent-information"
                  variant="outlined"
                sx={{
                  p: 1.75,
                  borderRadius: "18px",
                  backgroundColor: "rgba(255,255,255,0.84)",
                  borderColor: "rgba(255, 132, 76, 0.10)",
                  boxShadow: "0 14px 30px rgba(236, 123, 55, 0.05)",
                }}
              >
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#202531", mb: 1 }}>
                  Agent Information
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Duration</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedAgent.durationMs}ms</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Model</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedAgent.promptContent?.llmModel || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Type</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedAgent.promptContent?.type || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Timestamp</Typography>
                    <Typography variant="body2" fontWeight={600}>{new Date(selectedAgent["@timestamp"]).toLocaleString()}</Typography>
                  </Grid>
                </Grid>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1, pt: 1 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={4} md={2}>
                      <Typography variant="caption" color="text.secondary" display="block">Prompt Tokens</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatNumber(selectedAgent.promptContent?.promptTokensFromResponse || 0)}</Typography>
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <Typography variant="caption" color="text.secondary" display="block">Completion Tokens</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatNumber(selectedAgent.promptContent?.completionTokensFromResponse || 0)}</Typography>
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <Typography variant="caption" color="text.secondary" display="block">Reasoning Tokens</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatNumber(selectedAgent.promptContent?.reasoningTokensFromResponse || 0)}</Typography>
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <Typography variant="caption" color="text.secondary" display="block">Search Calls</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatNumber(getSearchCallCount(selectedAgent.promptContent))}</Typography>
                    </Grid>
                    {(() => {
                      const cost = calculateCost(
                        selectedAgent.promptContent?.llmModel,
                        selectedAgent.promptContent?.promptTokensFromResponse,
                        selectedAgent.promptContent?.completionTokensFromResponse,
                        selectedAgent.promptContent?.reasoningTokensFromResponse,
                        getSearchCallCount(selectedAgent.promptContent)
                      );
                      return (
                        <>
                          <Grid item xs={4} md={2}>
                            <Typography variant="caption" color="text.secondary" display="block">Input Cost</Typography>
                            <Typography variant="body2" fontWeight={600}>{formatCurrency(cost.inputCost, 'USD')}</Typography>
                          </Grid>
                          <Grid item xs={4} md={2}>
                            <Typography variant="caption" color="text.secondary" display="block">Output Cost</Typography>
                            <Typography variant="body2" fontWeight={600}>{formatCurrency(cost.outputCost, 'USD')}</Typography>
                          </Grid>
                          <Grid item xs={4} md={2}>
                            <Typography variant="caption" color="text.secondary" display="block">Web Search Cost</Typography>
                            <Typography variant="body2" fontWeight={600}>{formatCurrency(cost.webSearchCost, 'USD')}</Typography>
                          </Grid>
                          <Grid item xs={4} md={2}>
                            <Typography variant="caption" color="text.secondary" display="block">Total Cost</Typography>
                            <Typography variant="body2" fontWeight={700} color="primary.main">{formatCurrency(cost.usd, 'USD')}</Typography>
                          </Grid>
                        </>
                      );
                    })()}
                  </Grid>
                  </Box>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid rgba(255, 132, 76, 0.10)",
            backgroundColor: "rgba(255,255,255,0.68)",
          }}
        >
          <Button
            onClick={handleCloseAgentDialog}
            size="small"
            sx={{
              minHeight: 40,
              px: 2.25,
              borderRadius: "12px",
              textTransform: 'none',
              fontWeight: 700,
              color: "#4b5563",
              border: "1px solid rgba(15, 23, 42, 0.10)",
              backgroundColor: "rgba(255,255,255,0.82)",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        TransitionComponent={Slide}
        transitionDuration={{ enter: 300, exit: 300 }}
        sx={{ width: '50%', zIndex: 1400 }}
        PaperProps={{ sx: { width: '50%' } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Request : {drawerAgent?.name}
          </Typography>
          <CollapsibleField
            jsonMode
            value={drawerAgent?.llmRequestFormated}
            onChange={() => { }}
            alwaysExpanded={true}
          />
        </Box>
      </Drawer>
    </Box>
  );
};

export default LogAgents;
